<template>
	<div class="space-y-4">
		<!-- Filters - Full Width Above Both Sections -->
		<div class="bg-white rounded-lg border border-gray-200 p-3 space-y-4 mb-3">
			<!-- Stock Status Filter -->
			<div>
				<div class="text-xs font-medium text-gray-700 mb-2">Filter by Stock Status:</div>
				<div class="flex flex-wrap gap-2">
					<button
						v-for="option in stockStatusOptions"
						:key="option.id"
						:class="[
							'px-3 py-1.5 text-xs rounded-md border transition-all',
							selectedStockStatuses.includes(option.id)
								? 'bg-mabey-600 text-white border-mabey-600'
								: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
						]"
						@click="toggleStockStatusFilter(option.id)">
						<font-awesome-icon
							:icon="selectedStockStatuses.includes(option.id) ? 'fas fa-check-square' : 'far fa-square'"
							class="mr-1" />
						{{ option.name }}
					</button>
				</div>
			</div>

			<!-- Item Status Filter -->
			<div>
				<div class="text-xs font-medium text-gray-700 mb-2">Filter Items:</div>
				<div class="flex flex-wrap gap-2">
					<button
						v-for="option in itemStatusOptions"
						:key="option.value"
						:class="[
							'px-3 py-1.5 text-xs rounded-md border transition-all',
							localItemStatusFilter.includes(option.value)
								? 'bg-mabey-600 text-white border-mabey-600'
								: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
						]"
						@click="toggleItemStatusFilter(option.value)">
						<font-awesome-icon
							:icon="
								localItemStatusFilter.includes(option.value)
									? 'fas fa-check-square'
									: 'far fa-square'
							"
							class="mr-1" />
						{{ option.label }}
						<span
							class="ml-1.5 px-1.5 py-0.5 rounded text-xs font-semibold"
							:class="
								localItemStatusFilter.includes(option.value)
									? 'bg-white/20'
									: 'bg-gray-100'
							">
							{{ getItemStatusCount(option.value) }}
						</span>
					</button>
				</div>
			</div>

			<!-- Search and Depot Filter - 50% Width Container -->
			<div class="flex flex-wrap gap-2 w-full">
				<div class="flex-1" style="min-width: 180px">
					<div class="relative">
						<input
							v-model="searchFilter"
							type="text"
							placeholder="Search by contract, item code, description or depot..."
							class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400" />
						<font-awesome-icon
							icon="fas fa-search"
							class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
					</div>
				</div>
				<div style="min-width: 130px">
					<select
						v-model="depotFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="">All Depots</option>
						<option v-for="depot in filterDepots" :key="depot" :value="depot">
							{{ depot }}
						</option>
					</select>
				</div>
				<div style="min-width: 130px">
					<select
						v-model="contractFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="">All Contracts</option>
						<option
							v-for="contract in filterContracts"
							:key="contract"
							:value="contract">
							{{ contract }}
						</option>
					</select>
				</div>
				<div style="min-width: 150px">
					<select
						v-model="shortageRequestFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="">All Requests</option>
						<option
							v-for="request in props.requests"
							:key="request.id"
							:value="request.id">
							{{ request.contract_number }} ({{ request.id.slice(-6) }})
						</option>
					</select>
				</div>
				<div style="min-width: 130px">
					<select
						v-model="dueInFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="all">All Due Dates</option>
						<option
							v-for="dueDate in dueDateOptions"
							:key="dueDate"
							:value="dueDate">
							{{ formatShortDate(dueDate) }}
						</option>
					</select>
				</div>
			</div>

			<div class="flex items-center justify-between mb-2">
				<div class="flex items-center space-x-4 text-xs">
					<div>
						<span class="text-gray-500">Items:</span>
						<span class="font-semibold ml-1">{{ allItemsCount }}</span>
					</div>
					<div>
						<span class="text-gray-500">Overdue:</span>
						<span class="font-semibold ml-1 text-red-600">{{ overdueCount }}</span>
					</div>
				</div>
				<div class="flex items-center gap-2">
					<button
						class="bg-mabey-600 text-white px-4 py-2 rounded text-xs font-bold hover:bg-mabey-700 transition-all flex items-center gap-1.5 shadow-md"
						@click="showStockAnalysis = !showStockAnalysis">
						<font-awesome-icon :icon="showStockAnalysis ? 'fas fa-eye-slash' : 'fas fa-chart-bar'" />
						<span>{{ showStockAnalysis ? 'Hide Stock Analysis' : 'Show Stock Analysis' }}</span>
					</button>
					<button
						v-if="
							searchFilter ||
							shortageRequestFilter ||
							depotFilter ||
							contractFilter ||
							dueInFilter !== 'all' ||
							selectedStockStatuses.join(',') !== defaultStockStatuses.join(',') ||
							localItemStatusFilter.join(',') !== defaultItemStatuses.join(',')
						"
						class="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors flex items-center space-x-1 whitespace-nowrap"
						@click="clearFilters">
						<font-awesome-icon icon="fas fa-times" />
						<span>Clear Filters</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Grid for Items Table and Stock Analysis -->
		<div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
			<!-- Left: Items Allocation Table -->
			<div :class="[showStockAnalysis ? 'lg:col-span-8' : 'lg:col-span-12', 'space-y-3 min-w-0']">
				<!-- Bulk Actions -->
				<div class="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-wrap items-center gap-y-3 gap-x-4 shadow-sm animate-in fade-in slide-in-from-top-1 duration-200">
					<div class="flex items-center gap-2 pr-4 border-r border-blue-200 h-8">
						<span class="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">{{ selectedRows.size }}</span>
						<span class="text-sm font-medium text-blue-800">Selected</span>
					</div>

					<!-- Group 1: From Depot & Target Date -->
					<div class="flex flex-wrap items-center gap-3 min-w-0">
						<div class="flex items-center gap-1.5">
							<label class="text-xs font-semibold text-blue-700 whitespace-nowrap">From Depot:</label>
							<select
								v-model="bulkFromDepot"
								:disabled="selectedRows.size === 0"
								class="text-sm border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[110px] max-w-[140px] disabled:opacity-50">
								<option value="">Select Depot</option>
								<option v-for="depot in depotOptions" :key="depot" :value="depot">
									{{ depot }}
								</option>
							</select>
						</div>

						<div class="flex items-center gap-1.5">
							<label class="text-xs font-semibold text-blue-700 whitespace-nowrap">Delivery:</label>
							<input
								v-model="bulkTargetDate"
								type="date"
								:disabled="selectedRows.size === 0"
								class="text-sm border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white disabled:opacity-50" />
						</div>
					</div>

					<!-- Group 2: Qty Alloc & Fulfillment Status -->
					<div class="flex flex-wrap items-center gap-3 min-w-0">
						<div class="flex items-center gap-1.5">
							<label class="text-xs font-semibold text-blue-700 whitespace-nowrap">Qty Alloc:</label>
							<div class="flex items-center bg-white border border-blue-300 rounded overflow-hidden" :class="{ 'opacity-50': selectedRows.size === 0 }">
								<button 
									title="Set to Requested Quantity"
									:disabled="selectedRows.size === 0"
									:class="[
										'px-2 py-1 text-[10px] font-bold transition-colors border-r border-blue-200 whitespace-nowrap',
										bulkAllocatedQty === 'req' 
											? 'bg-blue-600 text-white' 
											: 'bg-blue-50 text-blue-700 hover:bg-blue-100'
									]"
									@click="bulkAllocatedQty = 'req'">
									MATCH REQ
								</button>
								<input
									v-model.number="bulkAllocatedQtyInput"
									type="number"
									placeholder="Manual"
									:disabled="selectedRows.size === 0"
									class="w-16 text-sm px-2 py-1 focus:outline-none border-none disabled:cursor-not-allowed"
									@input="onBulkQtyInput" />
							</div>
						</div>

						<div class="flex items-center gap-1.5">
							<label class="text-xs font-semibold text-blue-700 whitespace-nowrap">Status:</label>
							<select
								v-model="bulkFulfillmentStatus"
								:disabled="selectedRows.size === 0"
								class="text-sm border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[150px] disabled:opacity-50">
								<option value="">No Change</option>
								<option
									v-for="option in editableItemStatusOptions"
									:key="option.value"
									:value="option.value">
									{{ option.label }}
								</option>
							</select>
						</div>
					</div>

					<div class="flex flex-wrap items-center gap-2 w-full justify-start">
						<button
							:disabled="selectedRows.size === 0 || (!bulkFromDepot && !bulkTargetDate && !bulkFulfillmentStatus && bulkAllocatedQty === '')"
							class="bg-blue-600 text-white px-3 py-1 rounded text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 shadow-sm"
							@click="applyBulkActions">
							<font-awesome-icon icon="fas fa-check" />
							Apply
						</button>

						<button
							:disabled="selectedRows.size === 0 || isOperationPending"
							class="bg-green-600 text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-md"
							@click="saveSelectedRows">
							<font-awesome-icon v-if="isOperationPending" icon="fas fa-spinner" spin />
							<font-awesome-icon v-else icon="fas fa-save" />
							Save Selected
						</button>
					</div>
				</div>

				<!-- Table -->
				<div class="bg-white rounded-lg border border-gray-200 overflow-hidden max-w-full">
					<!-- Top Scrollbar -->
					<div
						ref="topScrollContainer"
						class="overflow-x-auto overflow-y-hidden w-full border-b border-gray-100"
						@scroll="syncScroll(topScrollContainer, tableContainer)">
						<div class="h-px" style="min-width: 780px"></div>
					</div>

					<div
						ref="tableContainer"
						class="overflow-x-auto overflow-y-hidden w-full"
						@scroll="syncScroll(tableContainer, topScrollContainer)">
						<table class="w-full text-sm min-w-[780px]">
							<thead>
								<tr class="bg-gray-50 border-b border-gray-200">
									<th
										class="pl-3 pr-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
										style="width: 36px"
										@click.stop="toggleAllRows">
										<button
											class="text-gray-500 hover:text-mabey-600 transition-colors"
											:title="allExpanded ? 'Collapse All' : 'Expand All'">
											<font-awesome-icon
												:icon="
													allExpanded
														? 'fas fa-chevron-up'
														: 'fas fa-chevron-down'
												"
												class="text-sm" />
										</button>
									</th>
									<th
										class="pl-3 pr-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
										style="width: 36px"
										@click.stop="toggleAllSelection">
										<input
											type="checkbox"
											:checked="forceChecked || (selectedRows.size === flatRows.filter(row => isRowSelectable(row)).length && selectedRows.size > 0)"
											:indeterminate="!forceChecked && selectedRows.size > 0 && selectedRows.size < flatRows.filter(row => isRowSelectable(row)).length"
											class="w-4 h-4 text-mabey-600 border-gray-300 rounded focus:ring-mabey-500 cursor-pointer"
											@click.stop="toggleAllSelection" />
									</th>
									<th
										class="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
										Item
									</th>
									<th
										class="px-2 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 70px">
										Qty Req
									</th>
									<th
										class="px-2 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 70px">
										Qty Alloc
									</th>
									<th
										class="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide hidden md:table-cell"
										style="width: 90px">
										Destination
									</th>
									<th
										class="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 90px">
										Source
									</th>
									<th
										class="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 90px">
										Delivery
									</th>
									<th
										class="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 100px">
										Due Date
									</th>
									<th
										class="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide"
										style="width: 80px">
										Item Status
									</th>
								</tr>
							</thead>
							<tbody>
								<template v-for="(row, rowIndex) in flatRows" :key="row.itemKey">
									<tr
										v-if="showRequestGroupHeader(row, rowIndex)"
										class="bg-mabey-50 border-y border-mabey-200">
										<td colspan="10" class="px-5 py-3">
											<div class="flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
												<div class="flex flex-wrap items-center gap-x-6 gap-y-3">
													<div class="min-w-[120px]">
														<div class="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-mabey-700">
															<font-awesome-icon icon="fas fa-clipboard" class="mr-1" />
															Shortage request
														</div>
														<button
															class="font-mono text-sm font-bold text-gray-900 hover:text-mabey-700 hover:underline"
															:title="`Open shortage request ${row.requestId}`"
															@click.stop="openRequestDetail(row)">
															#{{ row.requestId.slice(-6) }}
															<font-awesome-icon icon="fas fa-up-right-from-square" class="ml-1 text-[9px]" />
														</button>
													</div>
													<div class="min-w-[120px] border-l border-mabey-200 pl-6">
														<div class="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
															<font-awesome-icon icon="fas fa-file-contract" class="mr-1" />
															Contract
														</div>
														<div class="text-sm font-semibold text-gray-900">{{ row.requestContractNumber }}</div>
													</div>
													<div class="border-l border-mabey-200 pl-6">
														<div class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Request status</div>
														<button
															:class="['inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold transition-all hover:shadow-md', getRequestStatusClass(row.requestStatus)]"
															@click.stop="openRequestDetail(row)">
															{{ row.requestStatus.toUpperCase() }}
														</button>
													</div>
													<div class="border-l border-mabey-200 pl-6">
														<div class="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Destination</div>
														<div class="text-sm font-semibold text-gray-900">{{ row.requestDepot }}</div>
													</div>
													<div class="border-l border-mabey-200 pl-6">
														<div class="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Items</div>
														<div class="text-sm font-semibold text-gray-900">{{ getRequestGroupItemCount(row.requestId) }}</div>
													</div>
												</div>
												<div class="flex items-center gap-3 border-l border-mabey-200 pl-6">
													<template v-if="getRequest(row.requestId)?.claimed_by">
														<div class="text-right">
															<div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Claimed by</div>
															<div class="text-xs font-medium text-gray-700">{{ getRequest(row.requestId)?.claimed_by?.name }}</div>
														</div>
														<span
															class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-mabey-600 text-[11px] font-bold text-white"
															:title="getRequest(row.requestId)?.claimed_by?.name">
															{{ getClaimantInitials(getRequest(row.requestId)?.claimed_by?.name) }}
														</span>
														<button
															v-if="isClaimedByCurrentUser(row.requestId)"
															:disabled="claimPendingRequestId === row.requestId"
															class="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-wait disabled:opacity-50"
															@click.stop="releaseClaim(row.requestId)">
															{{ claimPendingRequestId === row.requestId ? 'Releasing…' : 'Release' }}
														</button>
													</template>
													<div v-else class="flex items-center gap-3">
														<span class="text-xs text-gray-500">Not claimed</span>
														<button
														:disabled="claimPendingRequestId === row.requestId"
														class="rounded bg-mabey-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-mabey-700 disabled:cursor-wait disabled:opacity-50"
														@click.stop="claimRequest(row.requestId)">
														{{ claimPendingRequestId === row.requestId ? 'Claiming…' : 'Claim request' }}
														</button>
													</div>
												</div>
											</div>
										</td>
									</tr>
									<!-- Main Row -->
									<tr
										:class="[
											{ 'border-b-0': expandedRows.has(row.item.id), 'bg-blue-50/50': selectedRows.has(row.item.id) }
										]"
										class="hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100"
										@click="toggleRow(row.item.id)">
										<td
											class="pl-3 pr-3 py-2 text-center cursor-pointer hover:bg-gray-100 transition-colors"
											style="width: 36px"
											@click.stop="toggleRow(row.item.id)">
											<button
												class="text-gray-400 hover:text-gray-600 transition-colors">
												<svg
													class="mt-1.5 w-4 h-4 transition-transform"
													:class="{
														'rotate-90': expandedRows.has(row.item.id)
													}"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24">
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 5l7 7-7 7" />
												</svg>
											</button>
										</td>
										<td
											class="pl-3 pr-3 py-2 text-center cursor-pointer hover:bg-blue-50/50 transition-colors"
											style="width: 36px"
											@click.stop="isRowSelectable(row) && toggleSelection(row.item.id)">
											<input
												type="checkbox"
												:checked="selectedRows.has(row.item.id)"
												:disabled="!isRowSelectable(row)"
												:title="!isRowSelectable(row) ? 'This item cannot be edited' : 'Select for bulk actions'"
												class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
												@click.stop="isRowSelectable(row) && toggleSelection(row.item.id)"
												@change.stop />
										</td>
										<!-- Item Code / Description -->
										<td class="px-2 py-2">
											<div class="flex items-center space-x-1.5">
												<span class="font-medium text-gray-900 text-sm">{{
													row.item.item_code
												}}</span>
												<span
													v-if="shouldShowSplitBadge(row.item)"
													class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700"
													:title="
														row.item.split_from_id
															? 'Split line — different source depot'
															: 'Original — split across depots'
													">
													SPLIT
												</span>
											</div>
											<div
												class="text-xs text-gray-500 whitespace-normal leading-tight mt-0.5"
												:title="row.item.description">
												{{ row.item.description }}
											</div>
										</td>
										<!-- Quantity Requested -->
										<td
											class="px-2 py-2 text-right font-semibold text-gray-900">
											{{ row.item.quantity_requested }}
										</td>
										<!-- Quantity Allocated -->
										<td
											class="px-2 py-2 text-right font-semibold text-blue-700 bg-blue-50/30"
											title="Amount being allocated from source">
											{{ getAllocatedQuantityLabel(row) }}
										</td>
										<!-- Destination (Depot) -->
										<td class="px-2 py-2 text-center border-r border-gray-100 hidden md:table-cell">
											<span class="text-sm font-semibold text-gray-900">{{
												row.requestDepot
											}}</span>
										</td>
										<!-- Source Depot(s) -->
										<td class="px-2 py-2 text-center">
											<span
												v-if="getSourceDepotsLabel(row)"
												class="text-xs font-semibold text-gray-900">
												{{ getSourceDepotsLabel(row) }}
											</span>
											<span v-else class="text-xs text-gray-400 italic">—</span>
										</td>
									<!-- Target Delivery Date -->
									<td class="px-2 py-2 text-center">
										<div class="flex flex-col items-center">
											<span
												v-for="(date, idx) in Array.from(new Set(getSplitItems(row).map(si => si.item.target_delivery_date).filter(Boolean)))"
												:key="idx"
												class="text-xs font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded mb-0.5 last:mb-0">
												{{ formatDate(String(date)) }}
											</span>
											<span v-if="!getSplitItems(row).some(si => si.item.target_delivery_date)" class="text-xs text-gray-400 italic">—</span>
										</div>
									</td>
										<!-- Due Date -->
										<td class="px-2 py-2 text-center">
											<span
												:class="getDueDateClass(row)"
												class="text-sm font-bold whitespace-nowrap">
												{{ formatShortDate(row.item.required_by_date) }}
											</span>
										</td>
										<!-- Item Status -->
										<td class="px-1 py-2 text-center">
											<div class="flex flex-wrap gap-1 justify-center">
												<span
													v-for="status in getAllStatusesFromSplitItems(row)"
													:key="status"
													class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-tight"
													:class="{
														'bg-purple-100 text-purple-800':
															status === 'flagged-for-transfer',
														'bg-red-100 text-red-800':
															status === 'rejected',
														'bg-green-100 text-green-800':
															status === 'pending-fulfillment',
														'bg-blue-100 text-blue-800':
															status === 'fulfilled',
														'bg-yellow-100 text-yellow-800':
															status === 'part-fulfilled',
														'bg-gray-100 text-gray-600':
															status === 'unfulfilled' || !status
													}"
													:title="getFulfillmentStatusLabel(status)">
													<template v-if="status === 'flagged-for-transfer'">Transfer</template>
													<template v-else-if="status === 'rejected'">Rejected</template>
													<template v-else-if="status === 'pending-fulfillment'">Pending</template>
													<template v-else-if="status === 'fulfilled'">Fulfilled</template>
													<template v-else-if="status === 'part-fulfilled'">Part Fill</template>
													<template v-else>—</template>
												</span>
											</div>
										</td>
									</tr>
									<!-- Expandable Allocation Details -->
									<tr
										v-if="expandedRows.has(row.item.id)"
										class="bg-blue-50/30 border-b border-gray-100">
										<td colspan="10" class="px-4 py-3" @click.stop>
											<!-- Show all split items (original + split lines) -->
											<div
												v-for="(splitRow, splitIdx) in getSplitItems(row)"
												:key="splitRow.itemKey"
												class="mb-3 last:mb-0">
												<!-- Split item header (if multiple items) -->
												<div
													v-if="getSplitItemsCount(row) > 1"
													class="flex items-center space-x-2 mb-2">
													<span
														class="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-1 rounded">
														{{
															splitRow.item.split_from_id
																? `Split Line ${splitIdx}`
																: 'Original'
														}}
													</span>
													<span class="text-xs text-gray-500">{{
														splitRow.item.item_code
													}}</span>
												</div>
												<div class="flex flex-wrap items-end gap-3">
													<!-- Move Qty -->
													<div
														class="flex-shrink-0"
														style="min-width: 100px">
														<label
															class="block text-xs font-medium text-gray-700 mb-1"
															>Move Qty</label
														>
														<input
															v-model.number="
																getRowFormData(splitRow)
																	.allocation_quantity
															"
															type="number"
															min="0"
															:disabled="!isEditable(splitRow.item)"
															class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
													</div>
													<!-- From Depot -->
													<div
														class="flex-shrink-0"
														style="min-width: 180px">
														<label
															class="block text-xs font-medium text-gray-700 mb-1"
															>From Depot</label
														>
														<select
															v-model="
																getRowFormData(splitRow)
																	.allocation_from_depot
															"
															:disabled="!isEditable(splitRow.item)"
															class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed">
															<option value="">Select Depot</option>
															<optgroup
																v-if="getFilteredDepotOptions(row, splitRow).recommended.length > 0"
																label="★ Recommended">
																<option
																	v-for="option in getFilteredDepotOptions(row, splitRow).recommended"
																	:key="option.depot"
																	:value="option.depot"
																	class="font-semibold text-green-700">
																	{{ option.label }}
																</option>
															</optgroup>
															<optgroup
																v-if="getFilteredDepotOptions(row, splitRow).other.length > 0"
																:label="getFilteredDepotOptions(row, splitRow).recommended.length > 0 ? 'Other Depots' : 'All Depots'">
																<option
																	v-for="option in getFilteredDepotOptions(row, splitRow).other"
																	:key="option.depot"
																	:value="option.depot">
																	{{ option.label }}
																</option>
															</optgroup>
														</select>
													</div>
													<!-- Target Delivery Date -->
													<div
														class="flex-shrink-0"
														style="min-width: 140px">
														<label
															class="block text-xs font-medium text-gray-700 mb-1"
															>Target Delivery</label
														>
														<input
															v-model="
																getRowFormData(splitRow)
																	.target_delivery_date
															"
															type="date"
															:disabled="!isEditable(splitRow.item)"
															class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
													</div>
													<!-- Fulfillment Status -->
													<div
														class="flex-shrink-0"
														style="min-width: 170px">
														<label
															class="block text-xs font-medium text-gray-700 mb-1"
															>Status</label
														>
														<select
															v-model="getRowFormData(splitRow).fulfillment_status"
															:disabled="!isEditable(splitRow.item)"
															class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed">
															<option
																v-for="option in editableItemStatusOptions"
																:key="option.value"
																:value="option.value">
																{{ option.label }}
															</option>
														</select>
													</div>
													<!-- Replace Item -->
													<div class="flex-shrink-0">
														<label class="block text-xs font-medium text-gray-700 mb-1"
															>Replace</label
														>
														<button
															:disabled="
																replacingItemKey === splitRow.itemKey ||
																isOperationPending ||
																!canReplaceItem(splitRow.item)
															"
															class="px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center space-x-1.5 border"
															:class="
																replacingItemKey === splitRow.itemKey ||
																isOperationPending ||
																!canReplaceItem(splitRow.item)
																	? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
																	: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50 hover:border-amber-400'
															"
															title="Replace this item with another inspHire item"
															@click="openReplacementModal(splitRow)">
															<app-loader
																v-if="replacingItemKey === splitRow.itemKey"
																size="small"
																class="w-3 h-3" />
															<template v-else>
																<font-awesome-icon icon="fas fa-exchange-alt" />
																<span>Replace</span>
															</template>
														</button>
													</div>
													<!-- Remove Button (only for split lines) -->
													<div
														v-if="splitRow.item.split_from_id"
														class="flex-shrink-0">
														<button
															:disabled="
																removingItemKey ===
																	splitRow.itemKey ||
																isOperationPending ||
																!isEditable(splitRow.item)
															"
															class="px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center space-x-1.5"
															:class="
																removingItemKey ===
																	splitRow.itemKey ||
																isOperationPending ||
																!isEditable(splitRow.item)
																	? 'bg-gray-100 text-gray-500 cursor-not-allowed'
																	: 'bg-red-600 text-white hover:bg-red-700'
															"
															title="Remove this split line"
															@click="removeSplitLine(splitRow)">
															<app-loader
																v-if="
																	removingItemKey ===
																	splitRow.itemKey
																"
																size="small"
																class="w-3 h-3" />
															<template v-else>
																<font-awesome-icon
																	icon="fas fa-trash" /><span
																	>Remove</span
																>
															</template>
														</button>
													</div>
												</div>
											<!-- Pending Fulfillment Explanation -->
											<div
												v-if="!isEditable(splitRow.item)"
												class="text-xs text-gray-500 italic mt-2">
												This item is not in an editable status (only Unfulfilled, Flagged for Transfer or Rejected items can be edited)
											</div>
											</div>

											<!-- Action Buttons (after all split items) -->
											<div class="mt-4 pt-3 border-t border-gray-200 flex items-center justify-start space-x-3">
												<button
													:disabled="
														savingItemKey === row.itemKey ||
														isOperationPending
													"
													class="px-6 py-2 rounded text-sm font-semibold transition-colors flex items-center space-x-2 shadow-sm"
													:class="
														savingItemKey === row.itemKey ||
														isOperationPending
															? 'bg-gray-100 text-gray-500 cursor-not-allowed'
															: 'bg-green-600 text-white hover:bg-green-700'
													"
													@click="saveAllSplitItems(row)">
													<app-loader
														v-if="savingItemKey === row.itemKey"
														size="small"
														class="w-3 h-3" />
													<template v-else>
														<font-awesome-icon icon="fas fa-save" />
														<span>Save</span>
													</template>
												</button>
												<button
													:disabled="
														addingItemKey === row.itemKey ||
														isOperationPending
													"
													class="px-4 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-2 border"
													:class="
														addingItemKey === row.itemKey ||
														isOperationPending
															? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
															: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
													"
													title="Split to source from a different depot"
													@click="splitItem(row)">
													<app-loader
														v-if="addingItemKey === row.itemKey"
														size="small"
														class="w-3 h-3" />
													<template v-else>
														<font-awesome-icon icon="fas fa-code-branch" />
														<span>Split across Depots</span>
													</template>
												</button>
											</div>
										</td>
									</tr>
								</template>
								<tr v-if="flatRows.length === 0" class="text-center">
									<td colspan="13" class="px-4 py-12 text-gray-500">
										<font-awesome-icon
											icon="fas fa-inbox"
											class="text-gray-300 text-4xl mb-3 block mx-auto" />
										No items to allocate
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- Right: Stock Analysis -->
			<div v-if="showStockAnalysis" class="lg:col-span-4 min-w-0">
				<div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
					<div
						class="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
						<h3 class="text-sm font-semibold text-gray-900">Stock Analysis</h3>
						<button
							class="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:border-mabey-400 hover:text-mabey-600 transition-all flex items-center"
							@click="showStockGridModal = true">
							<font-awesome-icon icon="fas fa-th" class="mr-1" />
							View Grid
						</button>
					</div>

					<!-- Tabs -->
					<div class="border-b border-gray-200">
						<nav class="-mb-px flex space-x-6 px-3">
<!--							<button-->
<!--								:class="[-->
<!--									stockAnalysisTab === 'by-item'-->
<!--										? 'border-mabey-500 text-mabey-600'-->
<!--										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',-->
<!--									'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs transition-colors duration-200'-->
<!--								]"-->
<!--								@click="stockAnalysisTab = 'by-item'">-->
<!--								<div class="flex flex-col items-center">-->
<!--									<font-awesome-icon icon="fas fa-boxes" class="mb-1" />-->
<!--									<span>Stock by Item</span>-->
<!--									<span class="text-[10px] text-gray-400 mt-0.5"-->
<!--										>Top 3 depots</span-->
<!--									>-->
<!--								</div>-->
<!--							</button>-->
							<button
								:class="[
									stockAnalysisTab === 'by-depot'
										? 'border-mabey-500 text-mabey-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
									'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs transition-colors duration-200'
								]"
								@click="stockAnalysisTab = 'by-depot'">
								<div class="flex flex-col items-center">
									<font-awesome-icon icon="fas fa-warehouse" class="mb-1" />
									<span>Fulfillment</span>
									<span class="text-[10px] text-gray-400 mt-0.5"
									>% per depot</span
									>
								</div>
							</button>
							<button
								:class="[
									stockAnalysisTab === 'by-destination'
										? 'border-mabey-500 text-mabey-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
									'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs transition-colors duration-200'
								]"
								@click="stockAnalysisTab = 'by-destination'">
								<div class="flex flex-col items-center">
									<font-awesome-icon
										icon="fas fa-truck-arrow-right"
										class="mb-1" />
									<span>By Destination</span>
									<span class="text-[10px] text-gray-400 mt-0.5"
										>Items per depot</span
									>
								</div>
							</button>

						</nav>
					</div>

					<!-- Tab Content -->
					<div class="p-2">
						<app-loader v-if="stockDataLoading" size="small" />
						<div
							v-else-if="recommendedDepots.length === 0"
							class="text-xs text-gray-500 text-center py-4">
							No items to display
						</div>

						<!-- By Item Tab -->
						<div v-else-if="stockAnalysisTab === 'by-item'">
							<div class="overflow-x-auto">
								<table class="w-full text-xs">
									<thead>
										<tr class="bg-gray-50 border-b border-gray-200">
											<th
												class="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
												Item
											</th>
											<th
												class="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
												Contract
											</th>
											<th
												class="px-2 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
												Qty
											</th>
											<th
												class="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase"
												colspan="3">
												Recommended Depots
											</th>
										</tr>
									</thead>
									<tbody class="divide-y divide-gray-100">
										<tr
											v-for="rec in recommendedDepots"
											:key="`${rec.item_code}-${rec.contract_number}`"
											class="hover:bg-gray-50">
											<td class="px-2 py-2">
												<div class="font-semibold text-gray-900">
													{{ rec.item_code }}
												</div>
												<div
													class="text-[10px] text-gray-500 truncate"
													:title="rec.description">
													{{ rec.description }}
												</div>
											</td>
											<td class="px-2 py-2">
												<div class="text-xs font-medium text-mabey-700">
													{{ rec.contract_number }}
												</div>
												<div class="text-[10px] text-gray-500">
													{{ rec.destination_depot }}
												</div>
											</td>
											<td
												class="px-2 py-2 text-right font-semibold text-gray-900">
												{{ rec.quantity_required }}
											</td>
											<td class="px-2 py-2" colspan="3">
												<div
													v-if="rec.depots.length > 0"
													class="flex items-center space-x-2">
													<div
														v-for="(depot, idx) in rec.depots"
														:key="depot.depot"
														class="inline-flex flex-col px-2 py-1 rounded text-xs"
														:class="
															idx === 0 ? 'bg-green-50' : 'bg-gray-50'
														">
														<div class="flex items-center space-x-1">
															<span
																class="font-semibold"
																:class="
																	idx === 0
																		? 'text-green-700'
																		: 'text-gray-700'
																"
																>{{ depot.depot }}</span
															>
															<span class="text-gray-400">·</span>
															<span
																class="font-semibold"
																:class="
																	depot.available >=
																	rec.quantity_required
																		? 'text-green-600'
																		: 'text-orange-500'
																">
																{{ depot.available }}
															</span>
														</div>
														<div
															class="text-[10px] text-gray-500 mt-0.5">
															<span v-if="depot.distance !== null"
																>{{
																	Math.round(depot.distance)
																}}
																miles</span
															>
															<span v-else class="italic"
																>distance loading...</span
															>
														</div>
													</div>
												</div>
												<span v-else class="text-xs text-red-500"
													>No depots available</span
												>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- By Destination Tab -->
						<div v-else-if="stockAnalysisTab === 'by-destination'" class="space-y-2">
							<div
								v-for="dest in destinationBreakdown"
								:key="dest.depot"
								class="border rounded-md overflow-hidden">
								<div
									class="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
									@click="toggleDestination(dest.depot)">
									<div class="flex items-center space-x-2">
										<button
											class="text-gray-400 hover:text-gray-600 transition-colors">
											<svg
												class="w-4 h-4 transition-transform"
												:class="{
													'rotate-90': expandedDestinations.has(
														dest.depot
													)
												}"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24">
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 5l7 7-7 7" />
											</svg>
										</button>
										<font-awesome-icon
											icon="fas fa-warehouse"
											class="text-mabey-600" />
										<span class="text-sm font-semibold text-gray-900">{{
											dest.depot
										}}</span>
									</div>
									<div class="flex items-center space-x-3">
										<span class="text-xs text-gray-500"
											>{{ dest.uniqueItems }} items</span
										>
										<span
											class="px-2 py-1 rounded-full text-xs font-bold bg-mabey-100 text-mabey-700"
											>{{ dest.totalQuantity }} total</span
										>
									</div>
								</div>
								<div
									v-if="expandedDestinations.has(dest.depot)"
									class="px-3 py-2 border-t border-gray-200">
									<div class="space-y-1.5">
										<div
											v-for="item in dest.items"
											:key="item.item_code"
											class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
											<div class="flex-1 min-w-0">
												<div class="text-xs font-semibold text-gray-900">
													{{ item.item_code }}
												</div>
												<div
													class="text-[10px] text-gray-500 truncate"
													:title="item.description">
													{{ item.description }}
												</div>
											</div>
											<div class="flex items-center space-x-2 ml-2">
												<span class="text-xs font-bold text-gray-900">{{
													item.quantity
												}}</span>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div
								v-if="destinationBreakdown.length === 0"
								class="text-xs text-gray-500 text-center py-4">
								No destination data available
							</div>
						</div>

						<!-- By Depot Tab -->
						<div v-else-if="stockAnalysisTab === 'by-depot'" class="space-y-2">
							<!-- Sort Controls -->
							<div class="flex items-center justify-between px-1 mb-2">
								<span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Sort By:</span>
								<div class="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
									<button
										:class="[
											depotSortMode === 'percentage'
												? 'bg-white text-mabey-700 shadow-sm'
												: 'text-gray-500 hover:text-gray-700',
											'px-2 py-1 text-[10px] font-bold rounded-md transition-all'
										]"
										@click="depotSortMode = 'percentage'">
										Fulfillment %
									</button>
									<button
										:class="[
											depotSortMode === 'region'
												? 'bg-white text-mabey-700 shadow-sm'
												: 'text-gray-500 hover:text-gray-700',
											'px-2 py-1 text-[10px] font-bold rounded-md transition-all'
										]"
										@click="depotSortMode = 'region'">
										Region
									</button>
								</div>
							</div>

							<!-- Region-grouped view -->
							<template v-if="depotSortMode === 'region'">
								<div
									v-for="group in depotFulfillmentByRegion"
									:key="group.region"
									class="border border-gray-200 rounded-md overflow-hidden">
									<!-- Region Header -->
									<div
										class="flex items-center justify-between px-3 py-2 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors border-b border-gray-200"
										@click="toggleRegion(group.region)">
										<div class="flex items-center space-x-2">
											<svg
												class="w-4 h-4 text-gray-500 transition-transform"
												:class="{ 'rotate-90': expandedRegions.has(group.region) }"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24">
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 5l7 7-7 7" />
											</svg>
											<font-awesome-icon
												icon="fas fa-globe"
												class="text-gray-500 text-xs" />
											<span class="text-sm font-bold text-gray-700 uppercase tracking-wide">{{
												group.region
											}}</span>
											<span class="text-[10px] bg-white text-gray-600 px-1.5 py-0.5 rounded-full font-medium border border-gray-200">
												{{ group.depotCount }} {{ group.depotCount === 1 ? 'depot' : 'depots' }}
											</span>
										</div>
										<div
											class="text-sm font-semibold"
											:class="
												group.averagePercentage >= 75
													? 'text-green-600'
													: group.averagePercentage >= 50
														? 'text-orange-500'
														: 'text-red-500'
											">
											{{ group.averagePercentage }}% avg
										</div>
									</div>

									<!-- Depots within region -->
									<div v-if="expandedRegions.has(group.region)" class="bg-gray-50/50 p-2 space-y-2">
										<div
											v-for="depot in group.depots"
											:key="depot.depot"
											class="border rounded-md overflow-hidden bg-white">
											<div
												class="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
												@click="toggleDepot(depot.depot)">
												<div class="flex items-center space-x-2">
													<button
														class="text-gray-400 hover:text-gray-600 transition-colors">
														<svg
															class="w-4 h-4 transition-transform"
															:class="{ 'rotate-90': expandedDepots.has(depot.depot) }"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24">
															<path
																stroke-linecap="round"
																stroke-linejoin="round"
																stroke-width="2"
																d="M9 5l7 7-7 7" />
														</svg>
													</button>
													<span class="text-sm font-semibold text-gray-900">{{
														depot.depot
													}}</span>
													<span class="text-xs text-gray-500 ml-2"
														>{{ depot.itemsFulfilled }} full{{ depot.itemsPartiallyFulfilled > 0 ? `, ${depot.itemsPartiallyFulfilled} partial` : '' }}</span
													>
												</div>
												<div
													class="text-sm font-bold"
													:class="
														depot.percentage >= 75
															? 'text-green-600'
															: depot.percentage >= 50
																? 'text-orange-500'
																: 'text-red-500'
													">
													{{ depot.percentage }}%
												</div>
											</div>
											<div class="px-3 py-1.5">
												<div class="w-full bg-gray-200 rounded-full h-2">
													<div
														class="h-2 rounded-full transition-all"
														:class="
															depot.percentage >= 75
																? 'bg-green-600'
																: depot.percentage >= 50
																	? 'bg-orange-500'
																	: 'bg-red-500'
														"
														:style="{ width: depot.percentage + '%' }"></div>
												</div>
											</div>

											<!-- Expanded Item Details -->
											<div
												v-if="expandedDepots.has(depot.depot)"
												class="border-t border-gray-200 bg-white">
												<div v-if="depot.canFulfill.length > 0" class="px-3 py-2">
													<div class="flex items-center space-x-1.5 mb-2">
														<font-awesome-icon
															icon="fas fa-check-circle"
															class="text-green-600 text-xs" />
														<span class="text-xs font-semibold text-green-700"
															>Can Fulfill ({{ depot.canFulfill.length }})</span
														>
													</div>
													<div class="space-y-1">
														<div
															v-for="item in depot.canFulfill"
															:key="item.item_code"
															class="flex items-center justify-between py-1 px-2 rounded bg-green-50">
															<div class="flex-1 min-w-0">
																<div class="text-xs font-semibold text-gray-900">
																	{{ item.item_code }}
																</div>
																<div
																	class="text-[10px] text-gray-500 truncate"
																	:title="item.description">
																	{{ item.description }}
																</div>
															</div>
															<div class="flex items-center space-x-2 ml-2 text-xs">
																<span class="text-gray-500"
																	>Need:
																	<span class="font-semibold text-gray-900">{{
																		item.required
																	}}</span></span
																>
																<span class="text-gray-400">·</span>
																<span class="text-green-600"
																	>Have:
																	<span class="font-bold">{{ item.available }}</span></span
																>
															</div>
														</div>
													</div>
												</div>
												<div
													v-if="depot.canPartiallyFulfill.length > 0"
													class="px-3 py-2 border-t border-gray-100">
													<div class="flex items-center space-x-1.5 mb-2">
														<font-awesome-icon
															icon="fas fa-adjust"
															class="text-orange-500 text-xs" />
														<span class="text-xs font-semibold text-orange-700"
															>Can Partially Fulfill ({{ depot.canPartiallyFulfill.length }})</span
														>
													</div>
													<div class="space-y-1">
														<div
															v-for="item in depot.canPartiallyFulfill"
															:key="item.item_code"
															class="flex items-center justify-between py-1 px-2 rounded bg-orange-50">
															<div class="flex-1 min-w-0">
																<div class="text-xs font-semibold text-gray-900">
																	{{ item.item_code }}
																</div>
																<div
																	class="text-[10px] text-gray-500 truncate"
																	:title="item.description">
																	{{ item.description }}
																</div>
															</div>
															<div class="flex items-center space-x-2 ml-2 text-xs">
																<span class="text-gray-500"
																	>Need:
																	<span class="font-semibold text-gray-900">{{
																		item.required
																	}}</span></span
																>
																<span class="text-gray-400">·</span>
																<span class="text-orange-600"
																	>Have:
																	<span class="font-bold">{{ item.available }}</span></span
																>
																<span class="text-orange-700 font-semibold"
																	>{{ item.coveragePercentage }}%</span
																>
															</div>
														</div>
													</div>
												</div>
												<div
													v-if="depot.cannotFulfill.length > 0"
													class="px-3 py-2 border-t border-gray-100">
													<div class="flex items-center space-x-1.5 mb-2">
														<font-awesome-icon
															icon="fas fa-exclamation-circle"
															class="text-red-600 text-xs" />
														<span class="text-xs font-semibold text-red-700"
															>Cannot Fulfill ({{ depot.cannotFulfill.length }})</span
														>
													</div>
													<div class="space-y-1">
														<div
															v-for="item in depot.cannotFulfill"
															:key="item.item_code"
															class="flex items-center justify-between py-1 px-2 rounded bg-red-50">
															<div class="flex-1 min-w-0">
																<div class="text-xs font-semibold text-gray-900">
																	{{ item.item_code }}
																</div>
																<div
																	class="text-[10px] text-gray-500 truncate"
																	:title="item.description">
																	{{ item.description }}
																</div>
															</div>
															<div class="flex items-center space-x-2 ml-2 text-xs">
																<span class="text-gray-500"
																	>Need:
																	<span class="font-semibold text-gray-900">{{
																		item.required
																	}}</span></span
																>
																<span class="text-gray-400">·</span>
																<span
																	:class="
																		item.available > 0
																			? 'text-orange-600'
																			: 'text-red-600'
																	">
																	Have:
																	<span class="font-bold">{{ item.available }}</span>
																</span>
																<span
																	v-if="item.available > 0"
																	class="text-orange-600 font-semibold"
																	>(Short {{ item.shortfall }})</span
																>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</template>

							<!-- Flat list view (sorted by percentage) -->
							<div
								v-for="depot in depotFulfillment"
								v-else
								:key="depot.depot"
								class="border rounded-md overflow-hidden">
								<div
									class="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
									@click="toggleDepot(depot.depot)">
									<div class="flex items-center space-x-2">
										<button
											class="text-gray-400 hover:text-gray-600 transition-colors">
											<svg
												class="w-4 h-4 transition-transform"
												:class="{
													'rotate-90': expandedDepots.has(depot.depot)
												}"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24">
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 5l7 7-7 7" />
											</svg>
										</button>
										<span class="text-sm font-semibold text-gray-900">{{
											depot.depot
										}}</span>
										<span v-if="depot.region" class="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium border border-blue-100 ml-1">
											{{ depot.region }}
										</span>
										<span class="text-xs text-gray-500 ml-2"
											>{{ depot.itemsFulfilled }} full{{ depot.itemsPartiallyFulfilled > 0 ? `, ${depot.itemsPartiallyFulfilled} partial` : '' }}</span
										>
									</div>
									<div class="flex items-center space-x-2">
										<div
											class="text-sm font-bold"
											:class="
												depot.percentage >= 75
													? 'text-green-600'
													: depot.percentage >= 50
														? 'text-orange-500'
														: 'text-red-500'
											">
											{{ depot.percentage }}%
										</div>
									</div>
								</div>
								<div class="px-3 py-1.5">
									<div class="w-full bg-gray-200 rounded-full h-2">
										<div
											class="h-2 rounded-full transition-all"
											:class="
												depot.percentage >= 75
													? 'bg-green-600'
													: depot.percentage >= 50
														? 'bg-orange-500'
														: 'bg-red-500'
											"
											:style="{ width: depot.percentage + '%' }"></div>
									</div>
								</div>

								<!-- Expanded Item Details -->
								<div
									v-if="expandedDepots.has(depot.depot)"
									class="border-t border-gray-200 bg-white">
									<!-- Can Fulfill Section -->
									<div v-if="depot.canFulfill.length > 0" class="px-3 py-2">
										<div class="flex items-center space-x-1.5 mb-2">
											<font-awesome-icon
												icon="fas fa-check-circle"
												class="text-green-600 text-xs" />
											<span class="text-xs font-semibold text-green-700"
												>Can Fulfill ({{ depot.canFulfill.length }})</span
											>
										</div>
										<div class="space-y-1">
											<div
												v-for="item in depot.canFulfill"
												:key="item.item_code"
												class="flex items-center justify-between py-1 px-2 rounded bg-green-50">
												<div class="flex-1 min-w-0">
													<div
														class="text-xs font-semibold text-gray-900">
														{{ item.item_code }}
													</div>
													<div
														class="text-[10px] text-gray-500 truncate"
														:title="item.description">
														{{ item.description }}
													</div>
												</div>
												<div
													class="flex items-center space-x-2 ml-2 text-xs">
													<span class="text-gray-500"
														>Need:
														<span class="font-semibold text-gray-900">{{
															item.required
														}}</span></span
													>
													<span class="text-gray-400">·</span>
													<span class="text-green-600"
														>Have:
														<span class="font-bold">{{
															item.available
														}}</span></span
													>
												</div>
											</div>
										</div>
									</div>
									<div
										v-if="depot.canPartiallyFulfill.length > 0"
										class="px-3 py-2 border-t border-gray-100">
										<div class="flex items-center space-x-1.5 mb-2">
											<font-awesome-icon
												icon="fas fa-adjust"
												class="text-orange-500 text-xs" />
											<span class="text-xs font-semibold text-orange-700"
												>Can Partially Fulfill ({{ depot.canPartiallyFulfill.length }})</span
											>
										</div>
										<div class="space-y-1">
											<div
												v-for="item in depot.canPartiallyFulfill"
												:key="item.item_code"
												class="flex items-center justify-between py-1 px-2 rounded bg-orange-50">
												<div class="flex-1 min-w-0">
													<div
														class="text-xs font-semibold text-gray-900">
														{{ item.item_code }}
													</div>
													<div
														class="text-[10px] text-gray-500 truncate"
														:title="item.description">
														{{ item.description }}
													</div>
												</div>
												<div
													class="flex items-center space-x-2 ml-2 text-xs">
													<span class="text-gray-500"
														>Need:
														<span class="font-semibold text-gray-900">{{
															item.required
														}}</span></span
													>
													<span class="text-gray-400">·</span>
													<span class="text-orange-600"
														>Have:
														<span class="font-bold">{{
															item.available
														}}</span></span
													>
													<span class="text-orange-700 font-semibold"
														>{{ item.coveragePercentage }}%</span
													>
												</div>
											</div>
										</div>
									</div>

									<!-- Cannot Fulfill Section -->
									<div
										v-if="depot.cannotFulfill.length > 0"
										class="px-3 py-2 border-t border-gray-100">
										<div class="flex items-center space-x-1.5 mb-2">
											<font-awesome-icon
												icon="fas fa-exclamation-circle"
												class="text-red-600 text-xs" />
											<span class="text-xs font-semibold text-red-700"
												>Cannot Fulfill ({{
													depot.cannotFulfill.length
												}})</span
											>
										</div>
										<div class="space-y-1">
											<div
												v-for="item in depot.cannotFulfill"
												:key="item.item_code"
												class="flex items-center justify-between py-1 px-2 rounded bg-red-50">
												<div class="flex-1 min-w-0">
													<div
														class="text-xs font-semibold text-gray-900">
														{{ item.item_code }}
													</div>
													<div
														class="text-[10px] text-gray-500 truncate"
														:title="item.description">
														{{ item.description }}
													</div>
												</div>
												<div
													class="flex items-center space-x-2 ml-2 text-xs">
													<span class="text-gray-500"
														>Need:
														<span class="font-semibold text-gray-900">{{
															item.required
														}}</span></span
													>
													<span class="text-gray-400">·</span>
													<span
														:class="
															item.available > 0
																? 'text-orange-600'
																: 'text-red-600'
														">
														Have:
														<span class="font-bold">{{
															item.available
														}}</span>
													</span>
													<span
														v-if="item.available > 0"
														class="text-orange-600 font-semibold"
														>(Short
														{{ item.shortfall }})</span
													>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Stock Grid Fullscreen Modal -->
	<teleport to="body">
		<div v-if="showStockGridModal" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="showStockGridModal = false"></div>
			<div
				class="relative bg-white rounded-lg shadow-xl max-w-[90vw] w-auto h-[85vh] flex flex-col min-w-[600px]">
				<div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
					<div>
						<h2 class="text-lg font-semibold text-gray-900">Stock Grid</h2>
						<p class="text-sm text-gray-500">Stock levels across all depots</p>
					</div>
					<button
						class="p-2 hover:bg-gray-100 rounded-md transition-colors"
						@click="showStockGridModal = false">
						<font-awesome-icon icon="fas fa-times" class="text-gray-500" />
					</button>
				</div>
				<div class="flex-1 overflow-auto p-4">
					<stock-list :stock-items="stockListItems" :show-shortage-button="false" />
				</div>
			</div>
		</div>
	</teleport>

	<stock-item-replacement-modal
		v-if="replacementRow"
		:is-open="Boolean(replacementRow)"
		:current-item="replacementRow.item"
		:saving="Boolean(replacingItemKey)"
		@close="closeReplacementModal"
		@replace="replaceItem" />

	<!-- Validation Warning Modal -->
	<teleport to="body">
		<div
			v-if="showValidationWarningModal"
			class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="cancelSaveWithWarnings"></div>
			<div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
				<!-- Modal Header -->
				<div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
					<div class="flex items-center space-x-2">
						<font-awesome-icon
							icon="fas fa-exclamation-triangle"
							class="text-amber-600" />
						<h2 class="text-lg font-semibold text-gray-900">Validation Warnings</h2>
					</div>
					<button
						class="p-2 hover:bg-gray-100 rounded-md transition-colors"
						@click="cancelSaveWithWarnings">
						<font-awesome-icon icon="fas fa-times" class="text-gray-500" />
					</button>
				</div>

				<!-- Modal Content -->
				<div class="p-6">
					<p class="text-sm text-gray-700 mb-4">
						The following validation warnings were found:
					</p>
					<div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
						<ul class="space-y-1">
							<li
								v-for="(warning, idx) in validationWarnings"
								:key="idx"
								class="text-sm text-amber-800 flex items-start space-x-2">
								<span class="text-amber-600 mt-0.5">•</span>
								<span>{{ warning }}</span>
							</li>
						</ul>
					</div>
					<p class="text-sm text-gray-600">
						These items may cause issues when creating a Transfer of Stock. Do you want
						to save anyway?
					</p>
				</div>

				<!-- Modal Footer -->
				<div
					class="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
					<button
						class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
						@click="cancelSaveWithWarnings">
						Cancel
					</button>
					<button
						class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors"
						@click="confirmSaveWithWarnings">
						Save Anyway
					</button>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script lang="ts" setup>
	import { ref, computed, watch, onMounted, nextTick, reactive } from 'vue'
	import type { StockShortageResponse, StockShortageItem } from '@/data/schema/stock-shortage'
	import type { Item } from '@/data/schema/item'
	import StockService from '@/services/stock'
	import useToaster from '@/stores/toaster'
	import useUserStore from '@/stores/auth'
	import AppLoader from '@/components/base/AppLoader.vue'
	import StockList from './StockList.vue'
	import StockItemReplacementModal from './StockItemReplacementModal.vue'
	import type { StockListItem } from './StockList.vue'
	import { formatDate } from '@/utils/date'

	interface FlatRow {
		item: StockShortageItem
		requestId: string
		requestStatus: StockShortageResponse['status']
		requestDepot: string
		requestContractNumber: string
		itemKey: string
		daysUntilDue: number
	}

	interface RecommendedDepot {
		item_code: string
		description: string
		quantity_required: number
		contract_number: string
		destination_depot: string
		depots: { depot: string; available: number; distance: number | null }[]
	}

	interface DepotFulfillmentItemDetail {
		item_code: string
		description: string
		required: number
		available: number
		fulfilledQuantity: number
		shortfall: number
		coveragePercentage: number
	}

	type StockStatusField =
		| 'quantity_available'
		| 'quantity_service'
		| 'quantity_repair'
		| 'allocation_quantity'

	const props = defineProps<{
		requests: StockShortageResponse[]
		itemStatusFilter: string[]
		focusFilter?: { requestId: string; itemCode: string } | null
	}>()

	const emit = defineEmits<{
		updated: [request: StockShortageResponse]
		openRequest: [request: StockShortageResponse]
	}>()

	const stockService = new StockService()
	const toaster = useToaster()
	const userStore = useUserStore()

	const expandedRows = ref<Set<string>>(new Set())
	const showStockAnalysis = ref(true)
	const showStockGridModal = ref(false)
	const stockData = ref<any[]>([])
	const stockDataLoading = ref(false)
	const stockAnalysisTab = ref<'by-item' | 'by-depot' | 'by-destination'>('by-depot')
	const depotSortMode = ref<'percentage' | 'region'>('percentage')
	const savingItemKey = ref<string | null>(null)
	const addingItemKey = ref<string | null>(null)
	const removingItemKey = ref<string | null>(null)
	const replacingItemKey = ref<string | null>(null)
	const replacementRow = ref<FlatRow | null>(null)
	const isOperationPending = ref(false)
	const claimPendingRequestId = ref<string | null>(null)

	const topScrollContainer = ref<HTMLElement | null>(null)
	const tableContainer = ref<HTMLElement | null>(null)

	const syncScroll = (source: HTMLElement | null, target: HTMLElement | null) => {
		if (source && target) {
			target.scrollLeft = source.scrollLeft
		}
	}
	const rowFormData = ref<Map<string, any>>(new Map())
	const searchFilter = ref<string>('')
	const shortageRequestFilter = ref<string>('')
	const depotFilter = ref<string>('')
	const contractFilter = ref<string>('')
	const dueInFilter = ref<string>('all')
	const expandedDepots = ref<Set<string>>(new Set())
	const expandedDestinations = ref<Set<string>>(new Set())
	const showValidationWarningModal = ref(false)
	const validationWarnings = ref<string[]>([])
	const pendingSaveRow = ref<FlatRow | null>(null)
	const isBulkSaving = ref(false)
	const selectedRows = ref<Set<string>>(new Set())
	const bulkFromDepot = ref('')
	const bulkTargetDate = ref('')
	const bulkFulfillmentStatus = ref<StockShortageItem['fulfillment_status'] | ''>('')
	const bulkAllocatedQty = ref<number | string>('')
	const bulkAllocatedQtyInput = ref<number | ''>('')

	const onBulkQtyInput = () => {
		if (bulkAllocatedQtyInput.value !== '') {
			bulkAllocatedQty.value = bulkAllocatedQtyInput.value
		} else {
			bulkAllocatedQty.value = ''
		}
	}

	const itemStatusOptions = [
		{ label: 'Unfulfilled', value: 'unfulfilled' },
		{ label: 'Flagged for Transfer', value: 'flagged-for-transfer' },
		{ label: 'Rejected', value: 'rejected' },
		{ label: 'Pending Fulfillment', value: 'pending-fulfillment' },
		{ label: 'Part Fulfilled', value: 'part-fulfilled' },
		{ label: 'Fulfilled', value: 'fulfilled' }
	]
	const editableItemStatusOptions = [
		{ label: 'Unfulfilled', value: 'unfulfilled' },
		{ label: 'Flagged for Transfer', value: 'flagged-for-transfer' },
		{ label: 'Rejected', value: 'rejected' }
	]
	const getFulfillmentStatusLabel = (status?: string) => {
		return itemStatusOptions.find((option) => option.value === status)?.label || 'Unfulfilled'
	}
	const defaultItemStatuses: string[] = ['unfulfilled']
	const localItemStatusFilter = ref<string[]>(props.itemStatusFilter && props.itemStatusFilter.length > 0 ? [...props.itemStatusFilter] : [...defaultItemStatuses])

	const stockStatusOptions: Array<{ id: 'quantity_available' | 'quantity_service' | 'quantity_repair' | 'allocation_quantity'; name: string }> = [
		{ id: 'quantity_available', name: 'Available' },
		{ id: 'quantity_service', name: 'In Service' },
		{ id: 'quantity_repair', name: 'In Repair' },
		{ id: 'allocation_quantity', name: 'Allocated' }
	]
	const defaultStockStatuses: StockStatusField[] = ['quantity_available', 'quantity_service']
	const selectedStockStatuses = ref<StockStatusField[]>([...defaultStockStatuses])

	const toggleStockStatusFilter = (status: StockStatusField) => {
		const index = selectedStockStatuses.value.indexOf(status)
		if (index > -1) {
			selectedStockStatuses.value.splice(index, 1)
		} else {
			selectedStockStatuses.value.push(status)
		}
	}

	const getFilteredStockQuantity = (stockItem: Record<string, unknown> | undefined): number => {
		if (!stockItem) return 0
		return selectedStockStatuses.value.reduce((total, statusKey) => {
			const value = Number(stockItem[statusKey] ?? 0)
			return total + (Number.isFinite(value) ? value : 0)
		}, 0)
	}

	// Load depot distances from cache immediately
	const loadCachedDistances = (): Record<string, any> => {
		try {
			const cached = localStorage.getItem('depotDistances')
			if (cached) {
				return JSON.parse(cached)
			}
		} catch (error) {
			console.error('Error loading cached distances:', error)
		}
		return {}
	}

	const depotDistances = ref<Record<string, any>>(loadCachedDistances())
	const currentItemCodes = ref<string[]>([])


	// Filter options - always based on all requests, not filtered data
	const filterDepots = computed(() => {
		const depots = new Set<string>()
		for (const req of props.requests) {
			if (req.depot) {
				depots.add(req.depot)
			}
		}
		return Array.from(depots).sort()
	})

	const filterContracts = computed(() => {
		const contracts = new Set<string>()
		for (const req of props.requests) {
			if (req.contract_number) {
				contracts.add(req.contract_number)
			}
		}
		return Array.from(contracts).sort()
	})

	const getDueDateValue = (dateString: string): string => {
		if (!dateString) return ''
		const directMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
		if (directMatch) return directMatch[1]

		const date = new Date(dateString)
		if (Number.isNaN(date.getTime())) return ''

		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const formatShortDate = (dateString: string): string => {
		if (!dateString) return '—'
		const date = new Date(dateString)
		if (Number.isNaN(date.getTime())) return '—'
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
	}

	const dueDateOptions = computed(() => {
		const dueDates = new Set<string>()
		for (const req of props.requests) {
			for (const item of req.items) {
				const dueDate = getDueDateValue(item.required_by_date)
				if (dueDate) {
					dueDates.add(dueDate)
				}
			}
		}
		return Array.from(dueDates).sort((a, b) => a.localeCompare(b))
	})

	const getAllStatusesFromSplitItems = (row: FlatRow): string[] => {
		const splitItems = getSplitItems(row)
		const statuses = new Set<string>()
		splitItems.forEach((item) => {
			const status = item.item.fulfillment_status || 'unfulfilled'
			statuses.add(status)
		})
		return Array.from(statuses)
	}

	// Create a reactive map of split items for proper reactivity
	const splitItemsMap = computed(() => {
		const map = new Map<string, FlatRow[]>()
		const now = Date.now()

		for (const request of props.requests) {
			request.items.forEach((item, index) => {
				// Include items with split_group_id OR items that are split from others
				if (!item.split_group_id && !item.split_from_id) return

				// Use split_group_id if available, otherwise create a temporary one for split_from_id items
				const groupId = item.split_group_id || `temp_${item.split_from_id}`

				if (!map.has(groupId)) {
					map.set(groupId, [])
				}

				const splitItems = map.get(groupId)!
				const requiredDate = new Date(item.required_by_date)
				const daysUntilDue = Math.ceil(
					(requiredDate.getTime() - now) / (1000 * 60 * 60 * 24)
				)
				splitItems.push({
					item,
					requestId: request.id,
					requestStatus: request.status,
					requestDepot: request.depot,
					requestContractNumber: request.contract_number,
					itemKey: item.id || `${request.id}_${index}`,
					daysUntilDue
				})
			})
		}

		// Sort each split group: original first, then split lines
		for (const items of map.values()) {
			items.sort((a, b) => {
				const aIsOriginal = !a.item.split_from_id
				const bIsOriginal = !b.item.split_from_id
				if (aIsOriginal && !bIsOriginal) return -1
				if (!aIsOriginal && bIsOriginal) return 1
				return a.daysUntilDue - b.daysUntilDue
			})
		}

		return map
	})

	// Helper to resolve the correct split group id for a row, avoiding ambiguous item_code matches
	const resolveSplitGroupId = (row: FlatRow): string | null => {
		// Prefer the group id on the row itself if present
		if (row.item.split_group_id) return row.item.split_group_id
		// Otherwise, find the exact item by id in the latest props to get an updated group id
		const req = props.requests.find((r) => r.id === row.requestId)
		if (!req) return null
		const exactItem = req.items.find((i) => i.id === row.item.id)
		return exactItem?.split_group_id || null
	}

	const getSplitItems = (row: FlatRow): FlatRow[] => {
		// Try to resolve a split group first (most reliable)
		const splitGroupId = resolveSplitGroupId(row)
		if (splitGroupId) {
			const fromMap = splitItemsMap.value.get(splitGroupId)
			if (fromMap && fromMap.length > 0) return fromMap
		}

		// Fallback: find the exact current row by id (not by item_code which can be duplicated)
		const currentRow = flatRows.value.find(
			(r) =>
				r.requestId === row.requestId && r.item.id === row.item.id && !r.item.split_from_id
		)
		if (currentRow) return [currentRow]
		return [row]
	}

	// Create a computed property for each row's split items count to avoid multiple calls
	const getSplitItemsCount = (row: FlatRow): number => {
		const splitGroupId = resolveSplitGroupId(row)
		if (!splitGroupId) return 1
		const splitItems = splitItemsMap.value.get(splitGroupId)
		if (splitItems && splitItems.length > 0) return splitItems.length
		// Fallback to 1 if group not found yet
		return 1
	}

	const getSourceDepotsLabel = (row: FlatRow): string => {
		const depots: string[] = []
		for (const sr of getSplitItems(row)) {
			const depot = sr.item.allocation_from_depot
			if (depot && !depots.includes(depot)) {
				depots.push(depot)
			}
		}
		return depots.length > 0 ? depots.join(' / ') : ''
	}

	const getAllocatedQuantityLabel = (row: FlatRow): string => {
		const quantities: string[] = []
		const splitItems = getSplitItems(row)
		for (const sr of splitItems) {
			const qty = sr.item.allocation_quantity
			quantities.push(qty !== undefined && qty !== null ? String(qty) : '—')
		}
		// If all split items have the same allocation quantity and it's just one, show it once
		// but usually for split items we want to see the distribution if they differ.
		const uniqueQuantities = Array.from(new Set(quantities))
		if (uniqueQuantities.length === 1 && splitItems.length === 1) {
			return uniqueQuantities[0]
		}
		return quantities.join(' / ')
	}

	const getStockAvailability = (itemCode: string, depot: string): number => {
		if (!stockData.value) return 0
		const stockItem = stockData.value.find((s: any) => s.grp_code === itemCode && s.depot === depot)
		return getFilteredStockQuantity(stockItem)
	}

	const shouldShowSplitBadge = (item: StockShortageItem): boolean => {
		if (!item.split_group_id) return false

		const request = props.requests.find((r) =>
			r.items.some((i) => i.split_group_id === item.split_group_id)
		)
		if (!request) return false

		// Count items with this split_group_id
		const itemsInGroup = request.items.filter((i) => i.split_group_id === item.split_group_id)
		return itemsInGroup.length > 1
	}

	const flatRows = computed((): FlatRow[] => {
		const result: FlatRow[] = []
		const now = Date.now()
		const processedGroups = new Set<string>()

		for (const req of props.requests) {
			req.items.forEach((item, index) => {
				// Skip split lines - they'll be shown in the expanded section of the original
				if (item.split_from_id) {
					return
				}

				// Skip if we've already processed this split group
				const groupId = item.split_group_id || `${req.id}_${index}`
				if (processedGroups.has(groupId)) {
					return
				}
				processedGroups.add(groupId)

				// Apply shortage request filter
				if (shortageRequestFilter.value && req.id !== shortageRequestFilter.value) {
					return
				}

				// Apply depot filter
				if (depotFilter.value && req.depot !== depotFilter.value) {
					return
				}

				// Apply contract filter
				if (contractFilter.value && req.contract_number !== contractFilter.value) {
					return
				}

				// Apply search filter
				if (searchFilter.value) {
					const search = searchFilter.value.toLowerCase()
					const matchesSearch =
						req.contract_number.toLowerCase().includes(search) ||
						req.depot.toLowerCase().includes(search) ||
						req.requested_by.toLowerCase().includes(search) ||
						item.item_code.toLowerCase().includes(search) ||
						item.description.toLowerCase().includes(search)
					if (!matchesSearch) {
						return
					}
				}

			// Apply item status filter
			if (localItemStatusFilter.value.length > 0 && !searchFilter.value) {
				// When item status filter has selections, apply it
				// For split items, check if ANY split item matches the filter
				const splitGroupId = item.split_group_id
				if (splitGroupId) {
					// Get all items in this split group
					const splitGroupItems = req.items.filter(
						(i) => i.split_group_id === splitGroupId
					)
					// Check if any item in the split group matches the filter
					const hasMatchingStatus = splitGroupItems.some((splitItem) => {
						const status = splitItem.fulfillment_status || 'unfulfilled'
						return localItemStatusFilter.value.includes(status)
					})
					if (!hasMatchingStatus) {
						return
					}
				} else {
					// Single item (not split) - check its status
					const itemStatus = item.fulfillment_status || 'unfulfilled'
					if (!localItemStatusFilter.value.includes(itemStatus)) {
						return
					}
				}
			}
			// If localItemStatusFilter is empty, show all items (no filtering)

				const daysUntilDue = Math.ceil(
					(new Date(item.required_by_date).getTime() - now) / 86400000
				)

				// Apply due date filter
				if (dueInFilter.value !== 'all') {
					const dueDate = getDueDateValue(item.required_by_date)
					if (dueDate !== dueInFilter.value) return
				}
				result.push({
					item,
					requestId: req.id,
					requestStatus: req.status,
					requestDepot: req.depot,
					requestContractNumber: req.contract_number,
					itemKey: item.id || `${req.id}_${index}`,
					daysUntilDue
				})
			})
		}
		const requestOrder = new Map(props.requests.map((request, index) => [request.id, index]))

		// Keep each shortage request together, preserving the request order from the API.
		return result.sort((a, b) => {
			const aGroup = a.item.split_group_id || ''
			const bGroup = b.item.split_group_id || ''

			const requestCompare =
				(requestOrder.get(a.requestId) ?? 0) - (requestOrder.get(b.requestId) ?? 0)
			if (requestCompare !== 0) return requestCompare

			// If both items are in the same split group, keep them together with original first
			if (aGroup && bGroup && aGroup === bGroup) {
				// Original item (no split_from_id) comes before split lines (has split_from_id)
				const aIsOriginal = !a.item.split_from_id
				const bIsOriginal = !b.item.split_from_id
				if (aIsOriginal && !bIsOriginal) return -1
				if (!aIsOriginal && bIsOriginal) return 1
				return 0 // Both are split lines or both are originals, keep current order
			}

			return a.daysUntilDue - b.daysUntilDue
		})
	})

	const showRequestGroupHeader = (row: FlatRow, rowIndex: number): boolean => {
		if (rowIndex === 0) return true
		return flatRows.value[rowIndex - 1]?.requestId !== row.requestId
	}

	const requestGroupItemCounts = computed(() => {
		const counts = new Map<string, number>()
		for (const row of flatRows.value) {
			counts.set(row.requestId, (counts.get(row.requestId) ?? 0) + 1)
		}
		return counts
	})

	const getRequestGroupItemCount = (requestId: string): number => {
		return requestGroupItemCounts.value.get(requestId) ?? 0
	}

	const getRequest = (requestId: string): StockShortageResponse | undefined => {
		return props.requests.find((request) => request.id === requestId)
	}

	const getClaimantInitials = (name?: string): string => {
		if (!name) return '?'
		const nameParts = name.trim().split(/\s+/).filter(Boolean)
		const lastName = nameParts[nameParts.length - 1]
		return `${nameParts[0]?.[0] ?? ''}${nameParts.length > 1 ? lastName?.[0] ?? '' : ''}`.toUpperCase()
	}

	const isClaimedByCurrentUser = (requestId: string): boolean => {
		const request = getRequest(requestId)
		return Boolean(
			request?.claimed_by_user_id &&
			userStore.user?.id &&
			request.claimed_by_user_id === userStore.user.id
		)
	}

	const unwrapShortageRequest = (response: unknown): StockShortageResponse => {
		return ((response as any)?.data?.data || (response as any)?.data || response) as StockShortageResponse
	}

	const claimRequest = async (requestId: string) => {
		claimPendingRequestId.value = requestId
		try {
			const response = await stockService.claimShortageRequest(requestId)
			emit('updated', unwrapShortageRequest(response))
			toaster.success({ text: 'Shortage request claimed' })
		} catch (error: any) {
			const currentRequest = error?.response?.data?.data
			if (currentRequest?.id) emit('updated', currentRequest)
			toaster.error({ text: error?.response?.data?.message || 'Failed to claim shortage request' })
		} finally {
			claimPendingRequestId.value = null
		}
	}

	const releaseClaim = async (requestId: string) => {
		claimPendingRequestId.value = requestId
		try {
			const response = await stockService.releaseShortageRequestClaim(requestId)
			emit('updated', unwrapShortageRequest(response))
			toaster.success({ text: 'Shortage request claim released' })
		} catch (error: any) {
			const currentRequest = error?.response?.data?.data
			if (currentRequest?.id) emit('updated', currentRequest)
			toaster.error({ text: error?.response?.data?.message || 'Failed to release shortage request claim' })
		} finally {
			claimPendingRequestId.value = null
		}
	}

	const depotOptions = computed(() => {
		const depots = new Set<string>()

		// Include depots from current stock data (most reliable source of all available depots)
		if (stockData.value && stockData.value.length > 0) {
			stockData.value.forEach((s: any) => {
				if (s.depot) {
					depots.add(s.depot)
				}
			})
		}

		// Also include depots from props.requests (destination depots)
		for (const req of props.requests) {
			if (req.depot) {
				depots.add(req.depot)
			}
		}

		// Also collect all depots that are mentioned in items as source depots (already allocated)
		props.requests.forEach((req) => {
			req.items.forEach((item) => {
				if (item.allocation_from_depot) {
					depots.add(item.allocation_from_depot)
				}
			})
		})

		return Array.from(depots).sort()
	})

	const stockListItems = computed((): StockListItem[] => {
		const itemMap = new Map<string, StockListItem>()
		const itemOrder: string[] = []

		for (const row of flatRows.value) {
			const existing = itemMap.get(row.item.item_code)
			if (existing) {
				existing.quantity_required += row.item.quantity_requested
			} else {
				itemMap.set(row.item.item_code, {
					item_code: row.item.item_code,
					description: row.item.description,
					quantity_required: row.item.quantity_requested
				})
				itemOrder.push(row.item.item_code)
			}
		}

		return itemOrder.map((code) => itemMap.get(code)!)
	})
	const loadStockData = () => {
		const itemCodes = stockListItems.value.map((i) => i.item_code)
		const itemCodesString = itemCodes.join(',')

		if (itemCodes.length === 0) {
			stockData.value = []
			currentItemCodes.value = []
			return
		}

		if (itemCodesString === currentItemCodes.value.join(',')) {
			return
		}

		currentItemCodes.value = itemCodes
		stockDataLoading.value = true
		stockService
			.getDepotStock(itemCodes)
			.then((res) => {
				stockData.value = res.data.data
			})
			.catch(() => {})
			.finally(() => {
				stockDataLoading.value = false
			})
	}

	onMounted(() => {
		loadDepotDistances()
	})

	const loadDepotDistances = async () => {
		// Skip if already loaded from cache
		if (Object.keys(depotDistances.value).length > 0) {
			return
		}

		try {
			const response = await stockService.getDepotDistances()
			const distanceArray = Array.isArray(response?.data)
				? response.data
				: response?.data?.data || []

			// Transform array into lookup map: { origin_depot: [{ depot: destination, distance_value: miles }] }
			const distanceMap: Record<string, Array<{ depot: string; distance_value: number }>> = {}

			for (const record of distanceArray) {
				const origin = record.origin_depot_key
				if (!distanceMap[origin]) {
					distanceMap[origin] = []
				}
				distanceMap[origin].push({
					depot: record.destination_depot_key,
					distance_value: parseFloat(record.distance_miles)
				})
			}

			depotDistances.value = distanceMap

			// Cache to localStorage
			try {
				localStorage.setItem('depotDistances', JSON.stringify(distanceMap))
			} catch (cacheError) {
				console.error('Error caching distances:', cacheError)
			}
		} catch (error) {
			console.error('Error fetching depot distances:', error)
			depotDistances.value = {}
		}
	}

	watch(
		stockListItems,
		() => {
			loadStockData()
		},
		{ immediate: true }
	)

	// Watch for focus filter from TOS Builder navigation
	watch(
		() => props.focusFilter,
		(filter) => {
			if (!filter) return

			// Set filters to find the item - show all statuses so the item is found
			localItemStatusFilter.value = [] // Empty array = all statuses
			shortageRequestFilter.value = filter.requestId
			searchFilter.value = filter.itemCode
			depotFilter.value = ''
			contractFilter.value = ''
			dueInFilter.value = 'all'

			// Expand the matching row after filters are applied
			nextTick(() => {
				const matchingRow = flatRows.value.find((r) =>
					r.item.item_code.toLowerCase().includes(filter.itemCode.toLowerCase())
				)
				if (matchingRow) {
					expandedRows.value.add(matchingRow.itemKey)
					expandedRows.value = new Set(expandedRows.value)
				}
			})
		}
	)

	const recommendedDepots = computed((): RecommendedDepot[] => {
		if (!stockData.value || stockData.value.length === 0) return []
		if (flatRows.value.length === 0) return []

		const itemsByContract = new Map<
			string,
			{
				item_code: string
				description: string
				quantity: number
				contract_number: string
				destination_depot: string
			}
		>()

		for (const row of flatRows.value) {
			const key = `${row.item.item_code}|${row.requestContractNumber}`
			if (itemsByContract.has(key)) {
				const existing = itemsByContract.get(key)!
				existing.quantity += row.item.quantity_requested
			} else {
				itemsByContract.set(key, {
					item_code: row.item.item_code,
					description: row.item.description,
					quantity: row.item.quantity_requested,
					contract_number: row.requestContractNumber,
					destination_depot: row.requestDepot
				})
			}
		}

		return Array.from(itemsByContract.values()).map((item) => {
			const destinationDepot = item.destination_depot

			const depotsWithStock = stockData.value
				.filter((s: any) => s.grp_code === item.item_code && getFilteredStockQuantity(s) > 0)
				.map((s: any) => {
					// Look up distance from destination depot to source depot
					const distancesFromDestination = depotDistances.value[destinationDepot] || []
					const distanceInfo = distancesFromDestination.find(
						(d: any) => d.depot === s.depot
					)

					return {
						depot: s.depot,
						available: getFilteredStockQuantity(s),
						distance: distanceInfo?.distance_value || null
					}
				})
				.sort((a, b) => {
					const aCanFulfill = a.available >= item.quantity
					const bCanFulfill = b.available >= item.quantity

					if (aCanFulfill && !bCanFulfill) return -1
					if (!aCanFulfill && bCanFulfill) return 1

					if (a.distance === null && b.distance === null) return b.available - a.available
					if (a.distance === null) return 1
					if (b.distance === null) return -1
					if (a.distance !== b.distance) return a.distance - b.distance
					return b.available - a.available
				})
				.slice(0, 3)

			return {
				item_code: item.item_code,
				description: item.description,
				quantity_required: item.quantity,
				contract_number: item.contract_number,
				destination_depot: item.destination_depot,
				depots: depotsWithStock
			}
		})
	})

	const destinationBreakdown = computed(() => {
		const breakdown = new Map<
			string,
			{
				depot: string
				items: Map<string, { item_code: string; description: string; quantity: number }>
			}
		>()

		for (const row of flatRows.value) {
			const depot = row.requestDepot
			if (!depot) continue

			if (!breakdown.has(depot)) {
				breakdown.set(depot, {
					depot,
					items: new Map()
				})
			}

			const depotData = breakdown.get(depot)!
			const itemKey = row.item.item_code

			if (depotData.items.has(itemKey)) {
				const existing = depotData.items.get(itemKey)!
				existing.quantity += row.item.quantity_requested
			} else {
				depotData.items.set(itemKey, {
					item_code: row.item.item_code,
					description: row.item.description,
					quantity: row.item.quantity_requested
				})
			}
		}

		return Array.from(breakdown.values())
			.map((depot) => ({
				depot: depot.depot,
				items: Array.from(depot.items.values()).sort((a, b) => b.quantity - a.quantity),
				uniqueItems: depot.items.size,
				totalQuantity: Array.from(depot.items.values()).reduce(
					(sum, item) => sum + item.quantity,
					0
				)
			}))
			.sort((a, b) => b.totalQuantity - a.totalQuantity)
	})

	const depotFulfillment = computed(() => {
		if (!stockData.value || stockData.value.length === 0 || stockListItems.value.length === 0)
			return []

		// Get unique depots
		const uniqueDepots = [...new Set(stockData.value.map((s: any) => s.depot))]
		const totalItems = stockListItems.value.length
		const totalRequiredQuantity = stockListItems.value.reduce(
			(sum, item) => sum + item.quantity_required,
			0
		)

		const result = uniqueDepots.map((depot) => {
			const canFulfill: DepotFulfillmentItemDetail[] = []
			const canPartiallyFulfill: DepotFulfillmentItemDetail[] = []
			const cannotFulfill: DepotFulfillmentItemDetail[] = []
			let fulfilledQuantity = 0

			// Find region from stockData
			const depotInfo = stockData.value.find((s: any) => s.depot === depot)
			const region = depotInfo?.depot_region || 'Unknown'

			stockListItems.value.forEach((item) => {
				const stockForItem = stockData.value.find(
					(s: any) => s.depot === depot && s.grp_code === item.item_code
				)

				const available = getFilteredStockQuantity(stockForItem)
				const fulfilledForItem = Math.min(Math.max(available, 0), item.quantity_required)
				const itemDetail: DepotFulfillmentItemDetail = {
					item_code: item.item_code,
					description: item.description,
					required: item.quantity_required,
					available,
					fulfilledQuantity: fulfilledForItem,
					shortfall: Math.max(item.quantity_required - available, 0),
					coveragePercentage:
						item.quantity_required > 0
							? Math.round((fulfilledForItem / item.quantity_required) * 100)
							: 0
				}
				fulfilledQuantity += fulfilledForItem

				if (available >= item.quantity_required) {
					canFulfill.push(itemDetail)
				} else if (available > 0) {
					canPartiallyFulfill.push(itemDetail)
				} else {
					cannotFulfill.push(itemDetail)
				}
			})

			return {
				depot,
				region,
				itemsFulfilled: canFulfill.length,
				itemsPartiallyFulfilled: canPartiallyFulfill.length,
				totalItems,
				fulfilledQuantity,
				totalRequiredQuantity,
				percentage:
					totalRequiredQuantity > 0
						? Math.round((fulfilledQuantity / totalRequiredQuantity) * 100)
						: 0,
				canFulfill,
				canPartiallyFulfill,
				cannotFulfill
			}
		})

		// Sort based on mode
		if (depotSortMode.value === 'region') {
			return result.sort((a, b) => {
				const regionSort = a.region.localeCompare(b.region)
				if (regionSort !== 0) return regionSort
				return b.percentage - a.percentage
			})
		}

		// Default: Sort by percentage descending (highest fulfillment first)
		return result.sort((a, b) => b.percentage - a.percentage)
	})

	const depotFulfillmentByRegion = computed(() => {
		const groups = new Map<string, typeof depotFulfillment.value>()
		for (const depot of depotFulfillment.value) {
			const existing = groups.get(depot.region)
			if (existing) {
				existing.push(depot)
			} else {
				groups.set(depot.region, [depot])
			}
		}

		return [...groups.entries()]
			.map(([region, depots]) => {
				const totalFulfilled = depots.reduce((sum, d) => sum + d.fulfilledQuantity, 0)
				const totalPossible = depots.reduce((sum, d) => sum + d.totalRequiredQuantity, 0)
				const averagePercentage =
					totalPossible > 0 ? Math.round((totalFulfilled / totalPossible) * 100) : 0
				return { region, depots, averagePercentage, depotCount: depots.length }
			})
			.sort((a, b) => a.region.localeCompare(b.region))
	})

	const expandedRegions = ref<Set<string>>(new Set())

	const toggleRegion = (region: string) => {
		if (expandedRegions.value.has(region)) {
			expandedRegions.value.delete(region)
		} else {
			expandedRegions.value.add(region)
		}
	}

	watch(depotSortMode, (mode) => {
		if (mode === 'region') {
			expandedRegions.value = new Set(depotFulfillmentByRegion.value.map((g) => g.region))
		}
	})

	watch(depotFulfillmentByRegion, (groups) => {
		if (depotSortMode.value !== 'region') return
		if (expandedRegions.value.size === 0 && groups.length > 0) {
			expandedRegions.value = new Set(groups.map((g) => g.region))
		}
	})

	const itemMatchesCountFilters = (
		req: StockShortageResponse,
		item: StockShortageItem
	): boolean => {
		if (shortageRequestFilter.value && req.id !== shortageRequestFilter.value) {
			return false
		}

		if (depotFilter.value && req.depot !== depotFilter.value) {
			return false
		}

		if (contractFilter.value && req.contract_number !== contractFilter.value) {
			return false
		}

		if (searchFilter.value) {
			const search = searchFilter.value.toLowerCase()
			const matchesSearch =
				req.contract_number.toLowerCase().includes(search) ||
				req.depot.toLowerCase().includes(search) ||
				req.requested_by.toLowerCase().includes(search) ||
				item.item_code.toLowerCase().includes(search) ||
				item.description.toLowerCase().includes(search)

			if (!matchesSearch) {
				return false
			}
		}

		if (dueInFilter.value !== 'all') {
			const dueDate = getDueDateValue(item.required_by_date)
			if (dueDate !== dueInFilter.value) return false
		}

		return true
	}

	const itemStatusCounts = computed(() => {
		const counts = new Map<string, number>()

		for (const req of props.requests) {
			req.items.forEach((item) => {
				if (!itemMatchesCountFilters(req, item)) {
					return
				}

				const itemStatus = item.fulfillment_status || 'unfulfilled'
				counts.set(itemStatus, (counts.get(itemStatus) || 0) + 1)
			})
		}

		return counts
	})

	const allItemsCount = computed(() => {
		return flatRows.value.length
	})

	const overdueCount = computed(() => {
		return flatRows.value.filter((row) => row.daysUntilDue < 0).length
	})

	const getItemStatusCount = (status: string): number => {
		return itemStatusCounts.value.get(status) || 0
	}

	const getDaysClass = (days: number): string => {
		if (days < 0) return 'text-red-600'
		if (days <= 2) return 'text-orange-500'
		if (days <= 7) return 'text-yellow-600'
		return 'text-green-600'
	}

	const hasUnfulfilledItem = (row: FlatRow): boolean => {
		return getSplitItems(row).some((splitRow) => (splitRow.item.fulfillment_status || 'unfulfilled') === 'unfulfilled')
	}

	const getDueDateClass = (row: FlatRow): string => {
		if (!hasUnfulfilledItem(row)) return 'text-gray-500'
		return getDaysClass(row.daysUntilDue)
	}

	const getRequestStatusClass = (status: string): string => {
		const classes: Record<string, string> = {
			pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
			rejected: 'bg-red-100 text-red-800 hover:bg-red-200',
			completed: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
		}
		return classes[status] || 'bg-gray-100 text-gray-800 hover:bg-gray-200'
	}

	const getDepotOptionsWithStock = (itemCode: string, destinationDepot: string, quantityRequired: number) => {
		if (!stockData.value || stockData.value.length === 0) {
			// Fallback to basic depot list if stock data not loaded
			return depotOptions.value.map((depot) => ({
				depot,
				label: depot,
				distance: null as number | null,
				available: 0,
				canFulfill: false,
				recommended: false
			}))
		}

		// Get unique depots from stock data
		const uniqueDepots = [...new Set(stockData.value.map((s: any) => s.depot))]
		const distancesFromDestination = depotDistances.value[destinationDepot] || []

		const options = uniqueDepots.map((depot) => {
			const stockForItem = stockData.value.find(
				(s: any) => s.depot === depot && s.grp_code === itemCode
			)
			const available = getFilteredStockQuantity(stockForItem)
			const distanceInfo = distancesFromDestination.find((d: any) => d.depot === depot)
			const distance = distanceInfo?.distance_value ?? null
			const canFulfill = available >= quantityRequired

			const distanceLabel = distance !== null ? ` · ${Math.round(distance)} mi` : ''
			return {
				depot,
				label: `${depot} (${available} avail${distanceLabel})`,
				distance,
				available,
				canFulfill,
				recommended: false
			}
		})

		// Sort: can fulfill first, then by distance ascending, then by available descending
		options.sort((a, b) => {
			if (a.canFulfill && !b.canFulfill) return -1
			if (!a.canFulfill && b.canFulfill) return 1
			if (a.distance === null && b.distance === null) return b.available - a.available
			if (a.distance === null) return 1
			if (b.distance === null) return -1
			if (a.distance !== b.distance) return a.distance - b.distance
			return b.available - a.available
		})

		// Mark top 3 depots with stock > 0 as recommended (matches Stock Analysis panel logic)
		let recommendedCount = 0
		for (const opt of options) {
			if (opt.available > 0 && recommendedCount < 3) {
				opt.recommended = true
				recommendedCount++
			}
		}

		return options
	}

	const getFilteredDepotOptions = (row: FlatRow, currentSplitRow: FlatRow) => {
		const allOptions = getDepotOptionsWithStock(
			currentSplitRow.item.item_code,
			row.requestDepot,
			currentSplitRow.item.quantity_requested
		)

		// Get all split items for this row
		const splitItems = getSplitItems(row)

		// Collect depots to exclude:
		// 1. The destination depot (contract depot)
		// 2. Depots already selected in other split lines
		const excludedDepots = new Set<string>()

		// Exclude destination depot
		excludedDepots.add(row.requestDepot)

		// Exclude depots already selected in other split lines
		splitItems.forEach((splitRow) => {
			// Don't exclude the current row's own selection
			if (splitRow.itemKey !== currentSplitRow.itemKey) {
				const selectedDepot =
					splitRow.item.allocation_from_depot ||
					getRowFormData(splitRow).allocation_from_depot
				if (selectedDepot) {
					excludedDepots.add(selectedDepot)
				}
			}
		})

		const filtered = allOptions.filter((option) => !excludedDepots.has(option.depot))

		// Split into recommended and other groups
		const recommended = filtered.filter((o) => o.recommended)
		const other = filtered.filter((o) => !o.recommended)

		return { recommended, other }
	}

	const getRowFormData = (row: FlatRow) => {
		if (!rowFormData.value.has(row.itemKey)) {
			let targetDeliveryDate = row.item.target_delivery_date
			if (targetDeliveryDate && typeof targetDeliveryDate === 'string') {
				targetDeliveryDate = targetDeliveryDate.split('T')[0] // Remove time part if present
			}

			const formData = reactive({
				allocation_from_depot: row.item.allocation_from_depot,
				allocation_quantity: row.item.allocation_quantity,
				target_delivery_date: targetDeliveryDate,
				fulfillment_status: row.item.fulfillment_status || 'unfulfilled'
			})

			rowFormData.value.set(row.itemKey, formData)
		}
		return rowFormData.value.get(row.itemKey)
	}

	const toggleItemStatusFilter = (status: string) => {
		const index = localItemStatusFilter.value.indexOf(status)
		if (index > -1) {
			localItemStatusFilter.value.splice(index, 1)
		} else {
			localItemStatusFilter.value.push(status)
		}
	}

	const clearFilters = () => {
		searchFilter.value = ''
		shortageRequestFilter.value = ''
		depotFilter.value = ''
		contractFilter.value = ''
		dueInFilter.value = 'all'
		selectedStockStatuses.value = [...defaultStockStatuses]
		localItemStatusFilter.value = [...defaultItemStatuses]
		clearSelection()
	}

	const getRowValidationWarnings = (row: FlatRow): string[] => {
		const splitItems = getSplitItems(row)
		const warnings: string[] = []

		// Filter out disabled rows — only validate items that are editable (unfulfilled or flagged-for-transfer)
		const editableItems = splitItems.filter((sr) => isEditable(sr.item))

		for (let i = 0; i < editableItems.length; i++) {
			const splitRow = editableItems[i]
			const formData = getRowFormData(splitRow)

			// Only validate if the item is flagged for transfer
			if (formData.fulfillment_status === 'flagged-for-transfer') {
				// Build item identifier with split line number if it's a split item
				const itemIdentifier = splitRow.item.split_from_id
					? `${splitRow.item.item_code} (Split Line ${i})`
					: splitRow.item.item_code

				if (!formData.target_delivery_date) {
					warnings.push(`${itemIdentifier}: Missing target delivery date`)
				}
				if (!formData.allocation_from_depot) {
					warnings.push(`${itemIdentifier}: Missing source depot`)
				} else {
					// Check stock availability
					const available = getStockAvailability(splitRow.item.item_code, formData.allocation_from_depot)
					const requested = formData.allocation_quantity
					if (requested > available) {
						warnings.push(`${itemIdentifier}: Only ${available} available at ${formData.allocation_from_depot} (Allocated: ${requested})`)
					}
				}
			}
		}
		return warnings
	}

	const confirmSaveWithWarnings = async () => {
		showValidationWarningModal.value = false
		
		if (isBulkSaving.value) {
			const rowsToSave = flatRows.value.filter((row) => selectedRows.value.has(row.item.id))
			await proceedWithBulkSave(rowsToSave)
			isBulkSaving.value = false
		} else {
			const row = pendingSaveRow.value
			if (!row) return
			await proceedWithSave(row)
		}

		// Clear pending state
		validationWarnings.value = []
		pendingSaveRow.value = null
	}

	const cancelSaveWithWarnings = () => {
		showValidationWarningModal.value = false
		validationWarnings.value = []
		pendingSaveRow.value = null
		isOperationPending.value = false
	}


	const forceChecked = ref(false)

	const toggleSelection = (id: string) => {
		const row = flatRows.value.find(r => r.item.id === id)
		if (row && !isRowSelectable(row)) return

		const newSet = new Set(selectedRows.value)
		if (newSet.has(id)) {
			newSet.delete(id)
		} else {
			newSet.add(id)
		}
		selectedRows.value = newSet

		if (selectedRows.value.size === 0) {
			clearSelection()
		}
	}

	const clearSelection = () => {
		selectedRows.value = new Set()
		forceChecked.value = false
		// Reset bulk action inputs when clearing selection
		bulkFromDepot.value = ''
		bulkTargetDate.value = ''
		bulkFulfillmentStatus.value = ''
		bulkAllocatedQty.value = ''
		bulkAllocatedQtyInput.value = ''
	}

	const toggleAllSelection = () => {
		const selectableRows = flatRows.value.filter(row => isRowSelectable(row))
		if (forceChecked.value || (selectedRows.value.size === selectableRows.length && selectableRows.length > 0)) {
			clearSelection()
		} else {
			selectedRows.value = new Set(selectableRows.map((row) => row.item.id))
			forceChecked.value = true
		}
	}

	const isEditable = (item: any) => {
		const status = item.fulfillment_status || 'unfulfilled'
		return status === 'unfulfilled' || status === 'flagged-for-transfer' || status === 'rejected'
	}

	const canReplaceItem = (item: StockShortageItem) => {
		return Boolean(item.id && isEditable(item) && !item.tos_no)
	}

	const openReplacementModal = (row: FlatRow) => {
		if (!canReplaceItem(row.item)) {
			toaster.warning({ text: 'This item cannot be replaced in its current status' })
			return
		}
		replacementRow.value = row
	}

	const closeReplacementModal = () => {
		if (replacingItemKey.value) return
		replacementRow.value = null
	}

	const isRowSelectable = (row: FlatRow) => {
		const splitItems = getSplitItems(row)
		return splitItems.some((si) => isEditable(si.item))
	}

	const applyBulkActions = () => {
		if (selectedRows.value.size === 0) return

		selectedRows.value.forEach((id) => {
			const row = flatRows.value.find((r) => r.item.id === id)
			if (row) {
				// Get all split items (original + splits)
				const splitItems = getSplitItems(row)
				splitItems.forEach((splitRow) => {
					// Only apply to editable items
					if (isEditable(splitRow.item)) {
						const formData = getRowFormData(splitRow)
						if (bulkFromDepot.value) {
							formData.allocation_from_depot = bulkFromDepot.value
						}
						if (bulkTargetDate.value) {
							formData.target_delivery_date = bulkTargetDate.value
						}
						if (bulkFulfillmentStatus.value) {
							formData.fulfillment_status = bulkFulfillmentStatus.value
							splitRow.item.fulfillment_status = bulkFulfillmentStatus.value
						}
						
						if (bulkAllocatedQty.value !== '') {
							if (bulkAllocatedQty.value === 'req') {
								// Match requested quantity for EACH individual split line
								formData.allocation_quantity = splitRow.item.quantity_requested
							} else {
								formData.allocation_quantity = Number(bulkAllocatedQty.value)
							}
						}
					}
				})
			}
		})

		toaster.success({
			text: `Applied bulk changes to ${selectedRows.value.size} items`
		})

		// Reset bulk actions after apply
		bulkFromDepot.value = ''
		bulkTargetDate.value = ''
		bulkFulfillmentStatus.value = ''
		bulkAllocatedQty.value = ''
		bulkAllocatedQtyInput.value = ''
	}

	const saveSelectedRows = async () => {
		if (selectedRows.value.size === 0) return

		const rowsToSave = flatRows.value.filter((row) => selectedRows.value.has(row.item.id))
		isOperationPending.value = true

		// Validate all rows first
		const allWarnings: string[] = []
		for (const row of rowsToSave) {
			const warnings = getRowValidationWarnings(row)
			if (warnings.length > 0) {
				const contractLabel = row.requestContractNumber ? ` [${row.requestContractNumber}]` : ''
				allWarnings.push(...warnings.map(w => `${w}${contractLabel}`))
			}
		}

		if (allWarnings.length > 0) {
			validationWarnings.value = allWarnings
			isBulkSaving.value = true
			showValidationWarningModal.value = true
			isOperationPending.value = false
			return
		}

		await proceedWithBulkSave(rowsToSave)
	}


	const proceedWithBulkSave = async (rowsToSave: FlatRow[]) => {
		isOperationPending.value = true
		let successCount = 0
		let failCount = 0

		for (const row of rowsToSave) {
			try {
				await proceedWithSave(row)
				successCount++
			} catch (error) {
				console.error(`Failed to save row ${row.item.id}:`, error)
				failCount++
			}
		}

		isOperationPending.value = false

		if (failCount === 0) {
			toaster.success({ text: `Successfully saved ${successCount} items` })
		} else {
			toaster.warning({
				text: `Saved ${successCount} items, but ${failCount} failed. Check console for details.`
			})
		}
	}

	const proceedWithSave = async (row: FlatRow) => {
		const request = props.requests.find((r) => r.id === row.requestId)
		if (!request) return

		savingItemKey.value = row.itemKey

		try {
			const splitItems = getSplitItems(row)

			// Filter out disabled rows — only save items that are editable from the sourcing grid
			const editableItems = splitItems.filter((sr) => isEditable(sr.item))

			// Save only editable items (skip disabled/read-only rows)
			for (const splitRow of editableItems) {
				if (!splitRow.item.id) {
					toaster.error({
						text: `Item ${splitRow.item.item_code} must be saved individually first`
					})
					savingItemKey.value = null
					isOperationPending.value = false
					return
				}

				const formData = getRowFormData(splitRow)

				const updateData: Partial<StockShortageItem> = {
					allocation_from_depot: formData.allocation_from_depot,
					allocation_quantity: formData.allocation_quantity,
					target_delivery_date: formData.target_delivery_date,
					fulfillment_status: formData.fulfillment_status || 'unfulfilled'
				}

				await stockService.updateShortageRequestItem(splitRow.item.id, updateData)
			}

			// Reload the request to get updated data
			const requestResponse = await stockService.getShortageRequest(request.id)
			const updatedRequest =
				(requestResponse as any)?.data?.data ||
				(requestResponse as any)?.data ||
				requestResponse
			emit('updated', updatedRequest)

			toaster.success({ text: `All items saved successfully` })
		} catch (error) {
			console.error('Error saving split items:', error)
			toaster.error({ text: 'Failed to save items' })
		} finally {
			savingItemKey.value = null
			isOperationPending.value = false
		}
	}

	const toggleRow = (itemId: string) => {
		if (expandedRows.value.has(itemId)) {
			expandedRows.value.delete(itemId)
		} else {
			expandedRows.value.add(itemId)
		}
		// Force reactivity
		expandedRows.value = new Set(expandedRows.value)
	}

	const allExpanded = computed(() => {
		if (flatRows.value.length === 0) return false
		return flatRows.value.every((row) => expandedRows.value.has(row.item.id))
	})

	const toggleAllRows = () => {
		if (allExpanded.value) {
			expandedRows.value = new Set()
		} else {
			const allIds = flatRows.value.map((row) => row.item.id)
			expandedRows.value = new Set(allIds)
		}
	}

	const toggleDepot = (depot: string) => {
		if (expandedDepots.value.has(depot)) {
			expandedDepots.value.delete(depot)
		} else {
			expandedDepots.value.add(depot)
		}
		// Force reactivity
		expandedDepots.value = new Set(expandedDepots.value)
	}

	const toggleDestination = (depot: string) => {
		if (expandedDestinations.value.has(depot)) {
			expandedDestinations.value.delete(depot)
		} else {
			expandedDestinations.value.add(depot)
		}
		// Force reactivity
		expandedDestinations.value = new Set(expandedDestinations.value)
	}

	const openRequestDetail = (row: FlatRow) => {
		const request = props.requests.find((r) => r.id === row.requestId)
		if (request) {
			emit('openRequest', request)
		}
	}

	const getReplacementItemCode = (item: Item) => item.item_code || item.code || item.item_no || ''

	const getReplacementDescription = (item: Item) => {
		return item.description || item.name || item.stockIdDescription || getReplacementItemCode(item)
	}

	const replaceItem = async (payload: { item: Item; reason: string }) => {
		const row = replacementRow.value
		if (!row || !row.item.id) return

		const request = props.requests.find((r) => r.id === row.requestId)
		if (!request) return

		const replacementCode = getReplacementItemCode(payload.item)
		if (!replacementCode) {
			toaster.error({ text: 'Replacement item is missing an item code' })
			return
		}

		if (replacementCode === row.item.item_code) {
			toaster.warning({ text: 'Select a different item to replace this line' })
			return
		}

		replacingItemKey.value = row.itemKey
		isOperationPending.value = true

		const originalCode = row.item.item_code
		const replacementDescription = getReplacementDescription(payload.item)

		try {
			const updateData: Partial<StockShortageItem> = {
				item_code: replacementCode,
				description: replacementDescription,
				item_weight: payload.item.weight || 0,
				allocation_from_depot: '',
				allocation_quantity: 0,
				target_delivery_date: '',
				tos_no: '',
				fulfillment_status: 'unfulfilled',
				stock_check_status: 'not_checked',
			}

			await stockService.updateShortageRequestItem(row.item.id, updateData)

			const replacementNote = [
				`[${new Date().toLocaleString('en-GB')}] Replaced item ${originalCode} with ${replacementCode}.`,
				`Reason: ${payload.reason}`
			].join(' ')
			const stockControllerNotes = [request.stock_controller_notes?.trim(), replacementNote]
				.filter(Boolean)
				.join('\n')

			await stockService.updateShortageRequest(request.id, {
				stock_controller_notes: stockControllerNotes
			})

			rowFormData.value.delete(row.itemKey)

			const requestResponse = await stockService.getShortageRequest(request.id)
			const updatedRequest =
				(requestResponse as any)?.data?.data ||
				(requestResponse as any)?.data ||
				requestResponse
			emit('updated', updatedRequest)

			toaster.success({ text: `Replaced ${originalCode} with ${replacementCode}` })
			replacementRow.value = null
		} catch (error) {
			console.error('Error replacing item:', error)
			toaster.error({ text: 'Failed to replace item' })
		} finally {
			replacingItemKey.value = null
			isOperationPending.value = false
		}
	}

	const splitItem = async (row: FlatRow) => {
		const request = props.requests.find((r) => r.id === row.requestId)
		if (!request) return

		if (!row.item.id) {
			toaster.error({ text: 'Save the item first before splitting' })
			return
		}

		if (isOperationPending.value) {
			toaster.warning({ text: 'Please wait for the current operation to complete' })
			return
		}

		addingItemKey.value = row.itemKey
		isOperationPending.value = true

		try {
			const splitGroupId = row.item.split_group_id || row.item.id

			// First, update the original item with split_group_id if it doesn't have one
			if (!row.item.split_group_id) {
				await stockService.updateShortageRequestItem(row.item.id, {
					split_group_id: splitGroupId
				})
			}

			// Then create the new split item
			const newItemData: Partial<StockShortageItem> = {
				item_code: row.item.item_code,
				description: row.item.description,
				quantity_required: row.item.quantity_required,
				quantity_available: row.item.quantity_available,
				quantity_requested: 0,
				required_by_date: row.item.required_by_date,
				type: row.item.type,
				shortage_amount: row.item.shortage_amount,
				item_weight: row.item.item_weight,
				fulfillment_status: 'unfulfilled',
				split_from_id: row.item.id,
				split_group_id: splitGroupId
			}

			await stockService.addItemToShortageRequest(request.id, newItemData)

			// Reload the request to get updated data from the server
			const requestResponse = await stockService.getShortageRequest(request.id)
			const updatedRequest =
				(requestResponse as any)?.data?.data ||
				(requestResponse as any)?.data ||
				requestResponse
			emit('updated', updatedRequest)

			// Ensure the row is expanded so the new split inputs are visible immediately
			try {
				const newSet = new Set(expandedRows.value)
				newSet.add(row.itemKey)
				expandedRows.value = newSet
			} catch {
				// no-op: expanding is a best-effort UI affordance
			}

			toaster.success({
				text: `Split line added for ${row.item.item_code} — set quantity and source depot on each line`
			})
		} catch (error) {
			console.error('Error splitting item:', error)
			toaster.error({ text: 'Failed to split item' })
		} finally {
			addingItemKey.value = null
			isOperationPending.value = false
		}
	}

	const removeSplitLine = async (row: FlatRow) => {
		if (!row.item.id || !row.item.split_from_id) {
			toaster.error({ text: 'Can only remove split lines, not original items' })
			return
		}

		if (
			!confirm(
				'Are you sure you want to remove this split line? This action cannot be undone.'
			)
		) {
			return
		}

		if (isOperationPending.value) {
			toaster.warning({ text: 'Please wait for the current operation to complete' })
			return
		}

		const request = props.requests.find((r) => r.id === row.requestId)
		if (!request) return

		removingItemKey.value = row.itemKey
		isOperationPending.value = true

		try {
			await stockService.deleteShortageRequestItem(request.id, row.item.id)

			// Reload the request to get updated data from the server
			const requestResponse = await stockService.getShortageRequest(request.id)
			const updatedRequest =
				(requestResponse as any)?.data?.data ||
				(requestResponse as any)?.data ||
				requestResponse
			emit('updated', updatedRequest)

			toaster.success({ text: 'Split line removed' })
		} catch (error) {
			console.error('Error removing split line:', error)
			toaster.error({ text: 'Failed to remove split line' })
		} finally {
			removingItemKey.value = null
			isOperationPending.value = false
		}
	}

	const saveAllSplitItems = async (row: FlatRow) => {
		const request = props.requests.find((r) => r.id === row.requestId)
		if (!request) return

		if (isOperationPending.value) {
			toaster.warning({ text: 'Please wait for the current operation to complete' })
			return
		}

		savingItemKey.value = row.itemKey
		isOperationPending.value = true

		try {
			const warnings = getRowValidationWarnings(row)

			// Show warnings and ask for confirmation
			if (warnings.length > 0) {
				validationWarnings.value = warnings
				pendingSaveRow.value = row
				showValidationWarningModal.value = true
				savingItemKey.value = null
				isOperationPending.value = false
				return
			}

			// Save items
			await proceedWithSave(row)
		} catch (error) {
			console.error('Error in saveAllSplitItems:', error)
		} finally {
			savingItemKey.value = null
			isOperationPending.value = false
		}
	}
</script>

<style scoped>
	table {
		border-collapse: separate;
		border-spacing: 0;
	}

	tr {
		transition: background-color 0.15s ease-in-out;
	}
</style>
